/**
 * IgelkottPlates product page runtime
 * ----------------------------------------------------------------
 * Ansvar:
 *  - Mounta Photo Sphere Viewer på hero-elementet med equirectangular
 *    stillbilden, auto-rotate som default
 *  - Hantera variant-toggle (uppdaterar pris + form-input + URL)
 *  - Like/Save → App Proxy med optimistic UI
 *  - "Watch full video" → lazy-mounta YouTube-iframe i modal
 *  - View-track impression
 *  - Allt loggas via window.igelkott.log
 */

(function () {
  'use strict';

  // +esm: jsdelivr skriver om de nakna importerna ('three',
  // '@photo-sphere-viewer/core') till URL:er. index.module.min.js
  // importerar dem nakna och föll utan import-map ("Module name 'three'
  // does not resolve", uppmätt 2026-10-28) — PSV-reserven startade aldrig.
  const PSV_CDN = 'https://cdn.jsdelivr.net/npm/@photo-sphere-viewer/core@5.13.3/+esm';
  const PSV_CSS = 'https://cdn.jsdelivr.net/npm/@photo-sphere-viewer/core@5.13.3/index.min.css';

  const log = (window.igelkott && window.igelkott.log) || {
    debug: () => {},
    info: () => {},
    warn: console.warn.bind(console),
    error: console.error.bind(console),
  };
  const fetchAuth =
    (window.igelkott && window.igelkott.fetch) || window.fetch.bind(window);

  let psvModulePromise = null;
  function loadPSV() {
    if (psvModulePromise) return psvModulePromise;
    if (!document.querySelector(`link[href="${PSV_CSS}"]`)) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = PSV_CSS;
      document.head.appendChild(css);
    }
    psvModulePromise = import(/* webpackIgnore: true */ PSV_CDN).catch((err) => {
      // ua: larmet 2026-10-28 gick inte att återskapa i WebKit med iPhone-UA.
      log.error('product.psv_load_failed', { error: err.message, ua: navigator.userAgent });
      throw err;
    });
    return psvModulePromise;
  }

  // -- Photo Sphere Viewer mount --------------------------------------------

  async function mountViewer(root, container, zoomLvl = 0, maxFov = 90) {
    const target = container || root.querySelector('[data-product-viewer-mount]');
    if (!target) return null;
    // Tre tier från Publisher Build #32+:
    //   8K (7680×1280) → desktop hi-DPI / final upgrade
    //   4K (3840×640)  → primary, första target efter low-res
    //   2K (1920×320)  → fast first-paint
    // Initial = 2K för snabb mount, uppgradera till 4K efter
    // panorama-loaded, sen 8K på hi-DPI desktop.
    const url8k = root.dataset.feedImage8k || '';
    const url4k = root.dataset.feedImage || '';
    const url2k = root.dataset.feedImage2k || url4k;
    const rpm = parseFloat(root.dataset.autorotateRpm || '0.6');

    if (!url4k && !url2k && !url8k) {
      log.warn('product.no_feed_image', { handle: root.dataset.handle });
      return null;
    }

    try {
      // Inget gyro-plugin: det startades aldrig (bara loggade gyro_ready),
      // dess +esm drog en egen kopia av three ("Multiple instances", ~0,6 MB
      // extra) och läser skärmvinkeln fel på iPad. iOS-reserven har eget
      // gyro (offerStillGyro). Borttaget 2026-10-28.
      const mod = await loadPSV();

      // Publisher Build #32+ levererar 6:1 horizon-strip (60° vertikal ×
      // 360° horisontal). 4K-tier är 3840×640. Räkna att full 2:1 sfär
      // ska vara w × w/2 och centrera bilden symmetriskt runt ekvator.
      // Svart fyllnad ovanför och under (canvasBackground='#000000').
      // Default-pitch=0 → kameran tittar lat=0 = bildens mitt = horisonten.
      //
      // Om Liquid inte gav explicita feed_image_width/height — anta 6:1
      // (Publisher-konventionen). 4K-tier-dimensionerna är säkra default
      // eftersom strippen alltid skalas till 3840×640 i ffmpeg.
      let wAttr = parseInt(root.dataset.feedImageWidth || '0', 10);
      let hAttr = parseInt(root.dataset.feedImageHeight || '0', 10);
      if (!wAttr || !hAttr) {
        wAttr = 3840;
        hAttr = 640;
      }
      let panoData = null;
      if (wAttr && hAttr && Math.abs(wAttr - 2 * hAttr) > 4) {
        const fullHeight = Math.round(wAttr / 2);
        panoData = {
          fullWidth: wAttr,
          fullHeight,
          croppedX: 0,
          croppedY: Math.round((fullHeight - hAttr) / 2),
          croppedWidth: wAttr,
          croppedHeight: hAttr,
        };
      }

      const viewer = new mod.Viewer({
        container: target,
        panorama: url2k,
        panoData,
        // Cropped-panorama: svart bakgrund för tomma sfärytor.
        canvasBackground: '#000000',
        touchmoveTwoFingers: false,
        mousewheel: false,
        navbar: false,
        loadingImg: null,
        defaultZoomLvl: zoomLvl,
        minFov: 50,
        maxFov,
      });

      viewer.addEventListener('ready', () => {
        root.dataset.viewerState = 'loaded';
        const hint = root.querySelector('[data-product-hint]');
        if (hint) hint.hidden = false;

        // Auto-rotate startar DIREKT (ingen 1s delay) — Eric vill se en
        // långsam rotation från första frame.
        if (rpm > 0) {
          try {
            if (viewer.startAutoRotate) {
              viewer.startAutoRotate({ rpm });
            } else if (viewer.autorotate && viewer.autorotate.start) {
              viewer.autorotate.start({ rpm });
            }
          } catch (e) {
            log.warn('product.autorotate_failed', { error: e.message });
          }
        }

        log.info('product.psv_mounted', { handle: root.dataset.handle });
      });

      viewer.addEventListener('panorama-loaded', () => {
        // Progressive enhancement via igelkott-tier helpers:
        //   1. Budget bestäms från Network Information API + DPR + saveData
        //   2. preloadAndDecode laddar + dekodar i bakgrunden
        //   3. scheduleIdle väntar tills main thread är ledig innan byte
        // Resultat: ingen mid-frame jank, ingen bandbredd-slöseri på
        // slow connections, snabb upgrade på fiber.
        const tier = window.igelkott && window.igelkott.tier;
        if (!tier || !viewer.setPanorama) return;

        const target = tier.pickTier({
          '2k': url2k,
          '4k': url4k,
          '8k': url8k,
        });
        if (!target) return; // inga URL:er över budget
        const targetUrl = target.url;
        const targetTier = target.tier;

        // Om current panorama redan är target = noop
        if (targetUrl === url2k && url2k === url4k && url4k === url8k) return;

        const upgrade = function (url, tierName) {
          tier.preloadAndDecode(url).then(function () {
            tier.scheduleIdle(function () {
              viewer.setPanorama(url, { transition: false }).catch(function () {});
              log.info('product.psv_upgraded', {
                handle: root.dataset.handle,
                tier: tierName,
              });
            });
          }).catch(function (err) {
            log.warn('product.psv_upgrade_failed', {
              handle: root.dataset.handle,
              tier: tierName,
              error: err.message,
            });
          });
        };

        // Två-stegs-upgrade: 2k (mount) → 4k → 8k. Hoppa direkt till
        // target om budget tillåter att skippa 4k-stationen (ger samma
        // upplevd kvalitet, sparar en setPanorama-cykel).
        if (targetTier === '8k' && url4k && url4k !== url2k) {
          upgrade(url4k, '4k');
          // 8k körs först när 4k:s scheduleIdle har triggat. Vi loopar
          // genom samma helper:n nästlat så main thread aldrig blockeras
          // av två preloadAndDecode parallellt.
          tier.scheduleIdle(
            function () {
              upgrade(url8k, '8k');
            },
            { timeoutMs: 6000 }
          );
        } else if (targetUrl !== url2k) {
          upgrade(targetUrl, targetTier);
        }
      });

      return viewer;
    } catch (err) {
      log.error('product.psv_mount_failed', {
        handle: root.dataset.handle,
        error: err.message,
      });
      return null;
    }
  }

  // -- Variant-toggle -------------------------------------------------------

  function setupVariants(root) {
    const fieldset = root.querySelector('[data-product-variants]');
    if (!fieldset) return;

    const formVariantInput = root.querySelector('[data-product-form-variant]');
    const buyBtn = root.querySelector('[data-product-buy]');
    // rev238 — bara ETT prisfält. Sekundärraden, incl.-etiketten och
    // excl.-raden är borttagna ur markupen; sidan visar ett pris och
    // överlåter momsen till checkout.
    const priceIncl = root.querySelector('[data-price-incl]');

    /*
      Eric 2026-07-30 (rev230) — prissträngarna kommer färdiga från
      Liquid, en per variant. JS räknar INTE på pengar och formaterar
      INTE tal; den byter bara ut sträng.

      Tidigare gjorde en lokal formatMoney() bådadera, med svenska
      separatorer och utan moms-avdrag. Resultatet var att ett klick på
      variantväljaren skrev över en korrekt server-render:
      "4,012.80 USD" blev "5.016 USD" för en amerikansk kund. Se
      motsvarande kommentar i sections/igelkott-product.liquid.
    */
    let priceMap = {};
    try {
      const mapEl = root.querySelector('[data-product-price-map]');
      if (mapEl) priceMap = JSON.parse(mapEl.textContent) || {};
    } catch (err) {
      log.error('product.price_map_parse_failed', {
        handle: root.dataset.handle,
        error: err.message,
      });
    }

    function applyPrice(variantId) {
      const entry = priceMap[String(variantId)];
      // Saknas variantet i kartan lämnar vi server-rendern orörd — ett
      // inaktuellt pris är bättre än ett påhittat.
      if (!entry) {
        log.warn('product.price_map_miss', {
          handle: root.dataset.handle,
          variant_id: variantId,
        });
        return;
      }
      if (priceIncl) priceIncl.textContent = entry.primary;
    }

    fieldset.addEventListener('change', (ev) => {
      const input = ev.target.closest('input[type="radio"]');
      if (!input) return;

      const variantId = input.value;
      formVariantInput.value = variantId;

      // Visuell selected-state på pillarna
      fieldset.querySelectorAll('label').forEach((lbl) => {
        lbl.classList.toggle('is-selected', lbl.contains(input));
      });

      // Priset byts synkront — ingen väntan på nätverket, ingen flimmer.
      applyPrice(variantId);

      // Hämta variant-data från Shopify (cachad i window.SECTION-data om
      // tillgängligt, annars fetch via .js-suffix). Eric 2026-05-11 —
      // bättre felhantering: kasta tidigt vid HTTP-fel + variant-saknad
      // så variant_fetch_failed-logg får actionable context.
      let _fetchStatus = null;
      fetchAuth(`${window.shopUrl}/products/${root.dataset.handle}.js`)
        .then((r) => {
          _fetchStatus = r.status;
          if (!r.ok) {
            throw new Error(`HTTP ${r.status}`);
          }
          return r.json();
        })
        .then((product) => {
          const variant = (product.variants || []).find((v) => String(v.id) === variantId);
          if (!variant) {
            throw new Error(`variant ${variantId} not in product.variants (count=${(product.variants || []).length})`);
          }

          // Priset sattes redan synkront från price-map:en ovan. Den här
          // fetchen finns kvar enbart för knappens tillgänglighets-state.

          // Buy-button-text
          if (buyBtn) {
            if (!variant.available) {
              buyBtn.textContent = 'Sold out';
              buyBtn.disabled = true;
            } else if (variant.price === 0) {
              const customer = (window.igelkott && window.igelkott.customer) || {};
              buyBtn.textContent = customer.logged_in
                ? 'Get free demo now'
                : 'Sign in for free demo';
              buyBtn.disabled = false;
            } else {
              buyBtn.textContent = 'Add to cart';
              buyBtn.disabled = false;
            }
          }

          log.info('product.variant_changed', {
            handle: root.dataset.handle,
            variant_id: variantId,
            variant_title: variant.title,
            available: variant.available,
            price: variant.price,
          });
        })
        .catch((err) => {
          log.error('product.variant_fetch_failed', {
            handle: root.dataset.handle,
            variant_id: variantId,
            http_status: _fetchStatus,
            error: err.message,
            shop_url: window.shopUrl,
          });
        });
    });
  }

  /*
    formatMoney() borttagen 2026-07-30 (rev230).

    Den hårdkodade svensk sifferkonvention (punkt som tusentals-
    avgränsare, noll decimaler) och returnerade brutto oavsett om
    besökaren skulle se moms eller inte. För en icke-EU-kund gav det
    "5.016 USD" där servern renderat "4,012.80 USD". All prisformatering
    görs numera av Shopify i Liquid och levereras via
    `data-product-price-map` — lägg inte tillbaka någon
    valutaformatering här.
  */

  // -- Free-demo (Sample-variant short-circuit) -----------------------------
  /*
    Eric 2026-04-28: när variant.price === 0 ska klick på Buy-knappen
    INTE lägga till i carten. Istället skickar vi direkt till
    /apps/igelkott/free-demo (samma route som tidigare bara var trigger:ad
    från cart). Anonymous users redirectas till login — krävs konto, inget
    email-prompt-flow för anonyma. Mejlet skickas till customer.email
    automatiskt utan att fråga.

    Success-state: Buy-knappen ersätts med "✓ Demo sent" + Download now-knapp
    + "Also sent to <email>"-rad. Cart-flödet rörs inte alls.
  */
  function setupFreeDemoButton(root) {
    const form = root.querySelector('[data-product-form]');
    const buyBtn = root.querySelector('[data-product-buy]');
    if (!form || !buyBtn) return;

    const handle = root.dataset.handle;
    const customer = (window.igelkott && window.igelkott.customer) || {};

    /*
      Cache variants vid mount så submit-handlern kan göra prischeck
      SYNKRONT. Tidigare bug: vi gjorde `await getCurrentVariant()` inuti
      submit-handlern, men async i submit-events returnerar Promise direkt
      → form-default-flödet hinner submit:a INNAN vår preventDefault
      körs (sidan navigerar till /cart före handlern resolved). Cachen
      löser det genom att alla data är tillgängliga synkront.

      Vid edge case där cache inte hann fylla (slow connection / offline):
      preventDefault som safety-net + visa fel istället för att råka
      submit:a sample-variant till /cart/add.
    */
    let variantsCache = null;
    (async () => {
      try {
        const res = await fetchAuth(
          `${window.shopUrl || ''}/products/${handle}.js`
        );
        if (!res.ok) return;
        const product = await res.json();
        variantsCache = product.variants;
        log.debug('product.variants_cached', {
          handle,
          count: variantsCache.length,
        });
      } catch (err) {
        log.warn('product.variants_cache_failed', {
          handle,
          error: err.message,
        });
      }
    })();

    /*
      Eric 2026-10-10 — plattor med både 16K360 och 8K360 har en sample per
      upplösning. Backend väljer sample-fil efter {want, native}; se
      proxy-backend/app/routes/free_demo.py (ResolutionChoice). Returnerar
      null för produkter med EN betald upplösning — då skickas fältet inte
      alls och demon fungerar som förut.
    */
    function resolutionOf(title) {
      const m = /(\d{1,2})K(?:360)?/i.exec(title || '');
      return m ? parseInt(m[1], 10) + 'K360' : null;
    }
    function resolutionChoice(variant) {
      if (!variantsCache || !variant) return null;
      const paid = [];
      variantsCache.forEach((v) => {
        if (v.price > 0 && !/sample/i.test(v.title || '')) {
          const r = resolutionOf(v.title);
          if (r && paid.indexOf(r) === -1) paid.push(r);
        }
      });
      if (paid.length < 2) return null;
      const native = paid.sort((a, b) => parseInt(b, 10) - parseInt(a, 10))[0];
      return { want: resolutionOf(variant.title) || native, native: native };
    }

    function getCurrentVariantSync() {
      if (!variantsCache) return null;
      const variantInput = root.querySelector('[data-product-form-variant]');
      const id = variantInput && variantInput.value;
      if (!id) return null;
      return variantsCache.find((v) => String(v.id) === String(id)) || null;
    }

    form.addEventListener('submit', async (ev) => {
      const variant = getCurrentVariantSync();
      if (variant && variant.price !== 0) {
        /*
          Betald variant — låt formen submit:a till /cart/add SOM HUVUDREGEL.
          Men först: client-side "max 1 per handle"-validering.

          Eric 2026-05-03: Shopify Function-validatorn (cart-validator) kan
          inte deploys pga CLI-bug i `shopify app function build` på macOS
          26.x. Tills den är på plats är detta enda försvaret mot duplicate
          handles. Bypass via direkt `/cart/add.js`-API är fortfarande
          möjlig (90% av riktiga kund-bypass:er går via knappen, så detta
          stoppar majoriteten).

          Pre-check: hämta /cart.js, se om någon line har samma product
          handle som vår. Om ja — blockera submit + visa error.
        */
        ev.preventDefault();
        ev.stopPropagation();
        try {
          const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
          if (cartRes.ok) {
            const cart = await cartRes.json();
            const dup = (cart.items || []).find(function (item) {
              return item.handle === handle;
            });
            if (dup) {
              log.info('product.cart_duplicate_blocked', {
                handle: handle,
                existing_variant_id: dup.variant_id,
                existing_variant_title: dup.variant_title,
                attempted_variant_id: variant.id,
              });
              // Eric 2026-10-10 — plattor som säljs i både 8K och 16K: säg
              // vilken upplösning som redan ligger i varukorgen. En
              // upplösning per plate och order (kassaregeln håller samma
              // linje). Samma variant igen → den gamla texten.
              const otherResolution =
                dup.variant_title && String(dup.variant_id) !== String(variant.id);
              renderFreeDemoError(
                root,
                otherResolution
                  ? 'You already have the ' + dup.variant_title + ' version of this plate in your cart. Each plate can be bought in one resolution per order — remove it from your cart to switch.'
                  : 'You already have this plate in your cart. Each plate can only be added once — different sizes count as the same plate. Empty your cart or pick another plate.'
              );
              return;
            }
          } else {
            log.warn('product.cart_check_failed', { status: cartRes.status });
          }
        } catch (err) {
          log.warn('product.cart_check_error', { error: err.message });
          // Cart-check failade (network) — låt form submit:a ändå istället
          // för att blockera giltiga köp pga ett intermittent fel. Worst
          // case: dubblett kan slinka igenom (samma som dagens state).
        }
        // Cart-check OK eller failade — submit form:n manuellt eftersom
        // vi har preventDefault:at ovan.
        form.submit();
        return;
      }
      if (!variant) {
        // Cache failade — säkerhets-blockera istället för att råka
        // submit:a en sample-variant till /cart/add. Användaren kan
        // refresh:a och försöka igen.
        ev.preventDefault();
        ev.stopPropagation();
        log.warn('product.free_demo_no_variant_cache', { handle });
        renderFreeDemoError(
          root,
          'Could not load variant data — please refresh and try again.'
        );
        return;
      }

      // variant.price === 0 → intercept form-submit + kör free-demo-flow
      ev.preventDefault();
      ev.stopPropagation();

      if (!customer.logged_in) {
        // Eric 2026-04-28: krävs konto. Skicka till login.
        // Eric 2026-05-10 (rev95 fix23/24) — använd NCA-syntax
        // customer_authentication/login?return_to=%2F. /account/login-
        // routen redirectas via shop.app-subdomain på New Customer
        // Accounts och DROPPAR querystring → return_url försvinner →
        // default Shopify-redirect till orders/account-dashboard.
        // customer_authentication/login respekterar return_to.
        log.info('product.free_demo_login_required', { handle });
        const shopDomain =
          (window.igelkott && window.igelkott.shop && window.igelkott.shop.domain) ||
          'igelkottstore.myshopify.com';
        window.location.href =
          'https://' + shopDomain + '/customer_authentication/login?return_to=%2F';
        return;
      }

      if (!customer.email) {
        // Sällsynt: inloggad men customer-objektet saknar email. Falla
        // tillbaka till en error-state istället för att tyst skicka utan
        // adress.
        log.error('product.free_demo_no_email', { handle });
        renderFreeDemoError(root, "Couldn't read your account email — please refresh and try again, or contact sales@igelkottplates.com.");
        return;
      }

      buyBtn.disabled = true;
      const originalLabel = buyBtn.textContent;
      buyBtn.textContent = 'Sending demo…';

      let sent = false;
      let links = [];

      try {
        const t0 = performance.now();
        const res = await fetchAuth('/apps/igelkott/free-demo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(Object.assign({
            handles: [handle],
            email: customer.email,
            // Eric 2026-04-29: skicka även namn så demo-mejlet kan
            // personaliseras ("Hi Eric" istället för "Hi there").
            // customer.first_name / last_name kommer från Liquid-templaten
            // (customer-object) och är "" om kunden inte fyllt i dem.
            first_name: customer.first_name || null,
            last_name: customer.last_name || null,
          }, (function () {
            const choice = resolutionChoice(variant);
            if (!choice) return {};
            const resolutions = {};
            resolutions[handle] = choice;
            return { resolutions: resolutions };
          })())),
          credentials: 'same-origin',
        });
        /*
          Eric 2026-04-29: läs response-body även vid !ok så vi får upstream-
          detail (proxy-backend bubblar upp aiosmtplib/RuntimeError-meddelanden
          via HTTPException.detail). Hjälper både console-debug och slutanvändar-
          meddelandet att vara konkret istället för "couldn't send the demo email".
        */
        if (!res.ok) {
          let detail = `HTTP ${res.status}`;
          try {
            const errBody = await res.json();
            if (errBody && errBody.detail) detail = errBody.detail;
          } catch (_) {
            // Inte JSON / tomt body — använd HTTP-status som default
          }
          /*
            Eric 2026-08-18 — skilj "gick fel" från "vet inte".

            Shopifys App Proxy släpper requesten efter 10 s och svarar
            504, men vår backend jobbar vidare och blir klar. Uppmätt
            2026-08-18 på stockholm-y046c0009_20211206082647: kunden fick
            BÅDE sitt demo-mejl OCH texten "Couldn't send the demo email".
            Det är aktivt osant och driver supportmejl från folk som
            redan har sin fil.

            Vid gateway-timeout, 502/504 eller vår egen
            `index_rebuild_in_progress` vet vi inte utfallet — då säger vi
            det, i stället för att påstå att sändningen misslyckades.
          */
          const undetermined =
            res.status === 502 ||
            res.status === 503 ||
            res.status === 504 ||
            res.status === 408 ||
            String(detail).startsWith('index_rebuild_in_progress');
          const e = new Error(detail);
          e.undetermined = undetermined;
          throw e;
        }
        const data = await res.json();
        log.info('product.free_demo_sent', {
          handle,
          email: customer.email,
          duration_ms: Math.round(performance.now() - t0),
        });
        sent = true;
        links = data.links || [];
      } catch (err) {
        /*
          Nätverksfel (fetch rejectar med TypeError) är också ett okänt
          utfall — requesten kan ha nått fram och behandlats klart.
        */
        const undetermined = !!err.undetermined || err.name === 'TypeError';
        /*
          Eric 2026-08-18 — `undetermined` loggas som WARN, inte error.

          Backend tvingar `category = "error"` for allt med level=error,
          vilket bade overkorde demo-mappningen OCH gav severity high →
          Telegram-larm. En Shopify-timeout ar ett normalt utfall, inte
          ett driftfel: mejlet ar sannolikt redan skickat. Uppmatt i
          testet 2026-08-18 — tva syntetiska prov gav tva high-larm.
        */
        const logAt = undetermined ? log.warn : log.error;
        logAt(
          undetermined ? 'product.free_demo_undetermined' : 'product.free_demo_failed',
          { handle, error: err.message, status_undetermined: undetermined }
        );
        buyBtn.disabled = false;
        buyBtn.textContent = originalLabel;
        renderFreeDemoError(
          root,
          undetermined
            ? `Your demo may already be on its way — check ${customer.email} before trying again. If nothing arrives in a few minutes, try once more or contact sales@igelkottplates.com.`
            : `Couldn't send the demo email (${err.message}). Try again, or contact sales@igelkottplates.com if it persists.`
        );
      }

      /*
        Eric 2026-08-18 — renderingen ligger UTANFÖR try:t.

        Tidigare anropades renderFreeDemoSuccess inne i samma try som
        fetchen, efter att `free_demo_sent` redan loggats. Ett kast i
        renderaren fångades då av catchen och rapporterades som ett
        sändningsfel — så `free_demo_failed` gick inte att lita på som
        signal för "sändning misslyckades". Nu kan de två inte förväxlas.
      */
      if (sent) {
        renderFreeDemoSuccess(root, customer.email, links);
      }
    });
  }

  function renderFreeDemoSuccess(root, email, links) {
    const buyBtn = root.querySelector('[data-product-buy]');
    if (!buyBtn) return;
    const downloadHref = links[0] || null;

    /*
      Eric:s spec 2026-04-28: single-click flow. Tidigare var det två
      klickar ("Download free demo" → "Demo sent" + "Download now") vilket
      Erics fru inte fattade. Nu: vi auto-öppnar share-länken i ny tab
      direkt när responsen kommer, och visar en kort confirmation med
      backup-länk om popup-blocker stoppade auto-open.
    */
    let autoOpened = false;
    if (downloadHref) {
      /*
        Eric 2026-08-18 — `noopener` borttaget ur feature-strangen.

        Per HTML-specen returnerar window.open() NULL nar noopener ar
        satt, aven nar fliken faktiskt oppnas. `autoOpened` blev darfor
        alltid false: grenen "Demo download opened" kunde aldrig visas,
        fallback-lanken renderades i onodan, och ett klick pa den gav en
        ANDRA flik mot samma delningslank. Uppmatt i test 2026-08-18 —
        tva identiska flikar mot /sharing/fZVvm6K4h.

        Skyddet mot reverse tabnabbing behalls genom att nolla opener
        manuellt, vilket bevarar returvardet.
      */
      const popup = window.open(downloadHref, '_blank');
      if (popup) {
        try {
          popup.opener = null;
        } catch (_) {
          // Cross-origin-restriktion i nagon browser — fliken ar anda
          // oppnad, och rel=noopener pa fallback-lanken tacker resten.
        }
      }
      autoOpened = !!popup;
    }

    const successBlock = document.createElement('div');
    successBlock.className = 'igelkott-product__free-demo-success';
    if (autoOpened) {
      successBlock.innerHTML = `
        <div class="igelkott-product__free-demo-status">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>Demo download opened</span>
        </div>
        <p class="igelkott-product__free-demo-email-note">Also sent to ${email} as backup. <a href="/account" class="igelkott-product__free-demo-email-change">Wrong email? Change here</a></p>
      `;
    } else if (downloadHref) {
      // Popup-blocker stoppade auto-open — visa fallback-länk så user
      // kan klicka manuellt. Detta händer ibland på Safari + iOS.
      successBlock.innerHTML = `
        <div class="igelkott-product__free-demo-status">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>Demo ready</span>
        </div>
        <a class="igelkott-product__free-demo-download" href="${downloadHref}" target="_blank" rel="noopener">
          Open download
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
        </a>
        <p class="igelkott-product__free-demo-email-note">Also sent to ${email}. <a href="/account" class="igelkott-product__free-demo-email-change">Wrong email? Change here</a></p>
      `;
    } else {
      // Backend returnerade ingen länk men 200 OK — degradera till
      // email-only success message.
      successBlock.innerHTML = `
        <div class="igelkott-product__free-demo-status">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>Demo sent</span>
        </div>
        <p class="igelkott-product__free-demo-email-note">Check ${email} for the download link. <a href="/account" class="igelkott-product__free-demo-email-change">Wrong email? Change here</a></p>
      `;
    }
    buyBtn.replaceWith(successBlock);
  }

  function renderFreeDemoError(root, msg) {
    /*
      Lägg fel-meddelande precis under buy-knappen utan att riva existerande
      state. Buy-knappen är redan tillbakaställd så användaren kan försöka
      igen. Tidigare error-element rensas så vi inte staplar dem.
    */
    const form = root.querySelector('[data-product-form]');
    if (!form) return;
    let errEl = form.querySelector('[data-product-error]');
    if (!errEl) {
      errEl = document.createElement('div');
      errEl.dataset.productError = '';
      errEl.className = 'igelkott-product__error';
      // Eric 2026-05-03 (QA-fix F-P1): a11y — screen readers ska announca
      // error-meddelandet när det dyker upp. role=alert + aria-live=assertive
      // gör att VoiceOver/NVDA läser texten direkt även om DOM-positionen
      // är efter user:s focus-context.
      errEl.setAttribute('role', 'alert');
      errEl.setAttribute('aria-live', 'assertive');
      form.appendChild(errEl);
    }
    errEl.textContent = msg;
  }

  // -- Like / Save ----------------------------------------------------------
  // LocalStorage-fallback (samma format som feed.js):
  //   `igelkott.like.local` / `igelkott.save.local` är JSON-arrays av
  //   handles. Källa-av-sanning är customer.metafields när proxy är live;
  //   localStorage är resilience-lager så state överlever page-reloads
  //   även när proxy är nere (= varför Eric tidigare tappade like när han
  //   tryckte save: vid proxy-fel revert:ade vi UI:n).

  function readActionLocalProduct(action) {
    try {
      return new Set(
        JSON.parse(localStorage.getItem(`igelkott.${action}.local`) || '[]')
      );
    } catch (_) {
      return new Set();
    }
  }

  function persistActionLocalProduct(action, handle, value) {
    try {
      const key = `igelkott.${action}.local`;
      const arr = JSON.parse(localStorage.getItem(key) || '[]');
      const set = new Set(arr);
      if (value) set.add(handle);
      else set.delete(handle);
      localStorage.setItem(key, JSON.stringify(Array.from(set)));
    } catch (_) {
      /* quota / disabled — silent */
    }
  }

  // Mobil: native share-sheet (mejl/meddelanden/AirDrop). Desktop: clipboard.
  // navigator.share finns på desktop-Safari också men öppnar en klumpig meny —
  // gate:a på touch. Svarar 'shared', 'copied' eller 'failed'.
  async function shareLink(url) {
    const isTouch = window.matchMedia('(pointer: coarse)').matches;
    if (isTouch && navigator.share) {
      try {
        await navigator.share({ url, title: document.title });
        return 'shared';
      } catch (_) {
        // Användaren avbröt share-sheet:en, eller den nekades —
        // fall igenom till clipboard.
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      return 'copied';
    } catch (_) {
      // Fallback för äldre/osäkra kontexter utan Clipboard API.
      const tmp = document.createElement('textarea');
      tmp.value = url;
      document.body.appendChild(tmp);
      tmp.select();
      let ok = false;
      try { ok = document.execCommand('copy'); } catch (_) { ok = false; }
      tmp.remove();
      return ok ? 'copied' : 'failed';
    }
  }

  // Eric 2026-11-03: "saknar share knapp mellan hjärta o collect". Fungerar utan
  // konto, som Share range (rev223). Är en range satt följer den med i länken.
  function setupProductShare(root) {
    const btn = root.querySelector('[data-product-share]');
    const lbl = btn && btn.querySelector('[data-product-share-label]');
    if (!lbl) return;
    let timer = null;
    btn.addEventListener('click', async (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const range = root.__igkTrimShareUrl ? root.__igkTrimShareUrl() : null;
      const url = range || (window.location.origin + window.location.pathname);
      log.info('product.share_clicked', { handle: root.dataset.handle || '', with_range: !!range });
      const how = await shareLink(url);
      lbl.textContent = how === 'shared' ? 'Shared' : how === 'copied' ? 'Link copied' : 'Copy failed';
      btn.classList.toggle('is-copied', how !== 'failed');
      btn.classList.toggle('is-err', how === 'failed');
      clearTimeout(timer);
      timer = setTimeout(() => {
        lbl.textContent = 'Share';
        btn.classList.remove('is-copied', 'is-err');
      }, 1800);
    });
  }

  function setupActions(root) {
    const handle = root.dataset.handle;
    const buttons = root.querySelectorAll('[data-product-action]');
    const customer = (window.igelkott && window.igelkott.customer) || {};

    // Hydra initial aria-pressed från localStorage (additive — om Liquid
    // redan satt true från customer.metafields, lämnar vi det. Om inte
    // men localStorage har handle-flaggan, sätt true.)
    const localLiked = readActionLocalProduct('like');
    const localSaved = readActionLocalProduct('save');
    buttons.forEach((btn) => {
      const action = btn.dataset.productAction;
      const alreadyTrue = btn.getAttribute('aria-pressed') === 'true';
      if (alreadyTrue) return;
      if (action === 'like' && localLiked.has(handle)) {
        btn.setAttribute('aria-pressed', 'true');
      }
      if (action === 'save' && localSaved.has(handle)) {
        btn.setAttribute('aria-pressed', 'true');
      }
    });

    buttons.forEach((btn) => {
      const action = btn.dataset.productAction;
      btn.addEventListener('click', async (ev) => {
        // Säkerställ att click på `<button>` inom product-form INTE
        // triggar form-submit (även om type="button" redan ska göra det).
        ev.preventDefault();
        ev.stopPropagation();

        const wasPressed = btn.getAttribute('aria-pressed') === 'true';
        const newPressed = !wasPressed;

        // Optimistic UI + localStorage persist (oavsett login-state).
        // Ingen revert vid proxy-failure — localStorage HÅLLER state.
        btn.setAttribute('aria-pressed', String(newPressed));
        persistActionLocalProduct(action, handle, newPressed);

        log.info(`product.${action}_clicked`, { handle, new_state: newPressed });

        if (!customer.logged_in) {
          // Inte inloggad — state lever lokalt. Tidigare redirected vi
          // till /account/login direkt vilket hindrade anonyma besökare
          // från att markera intresse. Nu låter vi local-state stå +
          // synkar vid framtida login.
          log.info('product.action_local_only_anonymous', { handle, action });
          return;
        }

        // Inloggad — försök sync till proxy. Misslyckas tyst om proxy
        // är nere; localStorage har redan state så UI förblir korrekt.
        try {
          const t0 = performance.now();
          const res = await fetchAuth(`/apps/igelkott/${action}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ handle, value: newPressed }),
            credentials: 'same-origin',
            keepalive: true,
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          log.info(`product.${action}_confirmed`, {
            handle,
            duration_ms: Math.round(performance.now() - t0),
          });
        } catch (err) {
          // INTE revert — localStorage är källa-av-sanning på klienten.
          log.warn(`product.${action}_proxy_failed`, {
            handle,
            error: err.message,
          });
        }
      });
    });
  }

  // -- View-tracking --------------------------------------------------------

  function trackView(handle) {
    // Skicka view-ping efter 3s dwell (filtrerar bort fly-by)
    setTimeout(() => {
      if (document.visibilityState !== 'visible') return;
      try {
        fetchAuth('/apps/igelkott/view-track', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ handle, dwell_ms: 3000, source: 'product_page' }),
          credentials: 'same-origin',
          keepalive: true,
        }).catch(() => {});
        log.info('product.view_tracked', { handle });
      } catch {
        /* tyst */
      }
    }, 3000);
  }

  // -- Video-modal (YouTube lazy-mount) -------------------------------------

  function setupVideo(root) {
    const trigger = root.querySelector('[data-product-video-trigger]');
    const modal = root.querySelector('[data-product-video-modal]');
    const close = root.querySelector('[data-product-video-close]');
    const frame = root.querySelector('[data-product-video-frame]');
    const youtubeId = root.dataset.youtubeId;
    if (!trigger || !modal || !frame || !youtubeId) return;

    function open() {
      // Bygg iframe på klick (inte före — det laddar Yt-trackers gratis)
      // Eric 2026-05-11 (rev134) — youtube-nocookie.com kringgår Safari
      // macOS cross-site-tracking-blockering som annars freezar videon.
      // Rev150: ingen autoplay-param. User klickar play i modal-spelaren.
      frame.innerHTML = `<iframe
        src="https://www.youtube-nocookie.com/embed/${encodeURIComponent(youtubeId)}?rel=0&modestbranding=1&fs=1"
        title="${root.querySelector('[data-product-title]')?.textContent || ''}"
        frameborder="0"
        allow="accelerometer; fullscreen; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowfullscreen></iframe>`;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      log.info('product.video_opened', {
        handle: root.dataset.handle,
        youtube_id: youtubeId,
      });
    }

    function closeModal() {
      frame.innerHTML = ''; // stoppar uppspelning + frigör resurser
      modal.hidden = true;
      document.body.style.overflow = '';
      log.debug('product.video_closed', { handle: root.dataset.handle });
    }

    trigger.addEventListener('click', open);
    close.addEventListener('click', closeModal);
    modal.addEventListener('click', (ev) => {
      if (ev.target === modal) closeModal();
    });
    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && !modal.hidden) closeModal();
    });
  }

  // -- Init -----------------------------------------------------------------

  /**
   * Parsa product.description (body_html) som Publisher genererar:
   *   <p><iframe src="...youtube.com/embed/X" ...></iframe></p>
   *   <ul>
   *     <li><strong>File Size:</strong> 58,30 GB</li>
   *     <li><strong>Duration:</strong> ...</li>
   *     ...
   *   </ul>
   *
   * Returnerar { youtubeId, specs: [{label, value}, ...] }.
   * Säker mot HTML-malformations — använder DOMParser inte regex.
   */
  function parseBodyHtml(root) {
    const source = root.querySelector('[data-product-body-source]');
    if (!source) return { youtubeId: null, specs: [] };

    let youtubeId = null;
    const iframe = source.querySelector('iframe[src*="youtube.com/embed/"]');
    if (iframe) {
      const m = iframe.src.match(/embed\/([a-zA-Z0-9_-]+)/);
      if (m) youtubeId = m[1];
      /*
        Eric 2026-05-11 (rev146) — Theis-diagnostic visade att Publisher
        injicerar <iframe src="https://www.youtube.com/embed/<id>"> i
        body_html som data-bärare. Vi extraherar bara youtubeId från
        src; själva iframe-elementet behövs inte i DOM. Safari bakgrunds-
        load:ar den vilket triggar cross-site-tracking-cookies + ev.
        WebKit GPU compositing-bug — exakt det rev134 löste för YT.Player-
        instansen.

        Lösning: `iframe.remove()` efter src-parsing. Eliminerar både
        nätverks-request till youtube.com (1 färre TCP-handshake +
        privacy-vinst) och Safari render-overhead. youtubeId-värdet är
        redan extraherat → ingen funktionalitetsförlust.
      */
      iframe.remove();
    }

    /*
      Eric 2026-07-31 (rev235) — plocka ut scenprosan.

      body_html har hittills bara innehållit `<p><iframe/></p>` följt av
      `<ul>` med spec-rader, och hela diven har varit dold eftersom den
      bara är en databärare. När IgelkottPublisher börjar lägga en
      scenbeskrivning där måste den bli SYNLIG — dold text ger noll
      kundvärde och räknas inte av Google.

      Vi läser efter `iframe.remove()` ovan, så den ursprungliga
      iframe-paragrafen är redan tom och faller bort av sig själv.
      Kvar blir exakt de stycken Publisher lagt till.

      `<li>`-parsningen nedan lämnas orörd — den fyller spec-tabellens
      luckor via fillSpecsFromBody och får inte störas.
    */
    const intro = [];
    source.querySelectorAll('p').forEach((p) => {
      const text = (p.textContent || '').trim();
      if (text) intro.push(text);
    });

    const specs = [];
    source.querySelectorAll('li').forEach((li) => {
      const strong = li.querySelector('strong');
      if (!strong) return;
      const label = (strong.textContent || '').replace(/:$/, '').trim();
      // Värdet är textNoden direkt efter <strong>; samla allt utom strong
      const value = Array.from(li.childNodes)
        .filter((n) => n !== strong)
        .map((n) => n.textContent || '')
        .join('')
        .trim();
      if (label && value) specs.push({ label, value });
    });

    log.info('product.body_parsed', {
      handle: root.dataset.handle,
      has_youtube: !!youtubeId,
      spec_count: specs.length,
      intro_paragraphs: intro.length,
    });
    return { youtubeId, specs, intro };
  }

  /**
   * Rendera scenprosan synligt ovanför spec-tabellen.
   *
   * Eric 2026-07-31 (rev235). Gör ingenting när prosan saknas — vilket
   * den gör på alla 412 produkter tills IgelkottPublisher börjar fylla
   * den. Containern lämnas då tom och dold, så sidan ser exakt likadan
   * ut som idag.
   *
   * Vi sätter textContent per stycke, aldrig innerHTML: texten kommer
   * från body_html som i sin tur är genererad, och ska renderas som text
   * även om något skulle råka innehålla markup.
   */
  function renderIntro(root, intro) {
    const host = root.querySelector('[data-product-intro]');
    if (!host) return;
    if (!intro || !intro.length) return;

    intro.forEach((stycke) => {
      const p = document.createElement('p');
      p.className = 'igelkott-product__intro-paragraph';
      p.textContent = stycke;
      host.appendChild(p);
    });
    // Dawn:s base.css döljer tomma div:ar (`div:empty { display: none }`)
    // med en specificitet som slår klassselektorer — samma fälla som
    // rev212 gick i med trim-panelen. Sätt display explicit när vi
    // faktiskt har innehåll.
    host.style.display = 'block';
    host.hidden = false;
  }

  /*
    Eric 2026-11-03 — filmremsa under beskrivningen.

    Serien är S### ur synth-SKU:n, annars kameraankaret (Y046, X010, P004)
    tillsammans med platsen: ankaret ensamt kolliderar mellan serier
    (feedback_shot_code_matching), eftersom reel-numret
    återstartar. Feed-indexet är redan hämtat av headern, så det kommer ur
    webbläsarens cache, och dolda produkter finns inte med.
  */
  function seriesKey(item) {
    if (item.synth_sku) return item.synth_sku.slice(0, 4);
    const title = item.title || '';
    const m = title.match(/(?:^|[^A-Za-z0-9])([A-Za-z]\d{3})(?=C\d|_|\s)/);
    if (!m) return null;
    const place = title.split(/[\s,]+/)[0].toLowerCase();
    return `${place}|${m[1].toUpperCase()}`;
  }

  function buildStripItem(item, isCurrent) {
    const li = document.createElement('li');
    li.className = 'igelkott-product__strip-item';
    const a = document.createElement('a');
    a.className = 'igelkott-product__strip-link';
    a.href = item.url;
    if (isCurrent) a.setAttribute('aria-current', 'page');
    const src = item.feed_image_2k || item.feed_image_4k || item.feed_image_8k;
    if (src) {
      const img = document.createElement('img');
      img.className = 'igelkott-product__strip-image';
      img.src = `${src}${src.includes('?') ? '&' : '?'}width=1200`;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      a.appendChild(img);
    }
    const name = document.createElement('span');
    name.className = 'igelkott-product__strip-name';
    name.textContent = item.title.replace(/\s+/g, ' ').trim();
    a.appendChild(name);
    li.appendChild(a);
    return li;
  }

  async function renderSeriesStrip(root) {
    const host = root.querySelector('[data-product-strip]');
    const list = host && host.querySelector('[data-product-strip-list]');
    if (!list) return;
    try {
      const res = await fetch('/apps/igelkott/feed-index.json');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { items = [] } = await res.json();
      const current = items.find((i) => i.handle === host.dataset.handle);
      const key = current && seriesKey(current);
      if (!key) return;
      const series = items
        .filter((i) => seriesKey(i) === key)
        .sort((a, b) => (a.synth_sku || a.title).localeCompare(b.synth_sku || b.title, 'en', { numeric: true }));
      // Publicerat två gånger (samma titel, två handles): en rad, och den
      // man står på vinner så att markeringen alltid finns.
      const unique = new Map();
      series.forEach((i) => {
        const name = i.title.replace(/\s+/g, ' ').trim();
        if (!unique.has(name) || i === current) unique.set(name, i);
      });
      if (unique.size < 2) return;

      unique.forEach((i) => list.appendChild(buildStripItem(i, i === current)));
      host.hidden = false;
      // scrollLeft, inte scrollIntoView: sidan ska inte hoppa vid laddning.
      const active = list.querySelector('[aria-current]');
      if (active) {
        const li = active.parentElement;
        list.scrollLeft = li.offsetLeft - (list.clientWidth - li.offsetWidth) / 2;
      }
    } catch (err) {
      log.warn('product.series_strip_failed', err);
    }
  }

  /**
   * Ersätt hero (PSV/bild) med YouTube-iframe på alla viewports — både
   * mobile och desktop. PSV/equirectangular-strippen behålls bara som
   * fallback när Publisher inte har genererat någon YouTube-ID:n.
   *
   * Watch-Video-button + lazy modal är fortfarande tillgängligt som
   * "öppna fullscreen"-flöde, men hero-positionen prioriterar always-
   * playing video när det finns.
   */
  /*
    YouTube IFrame Player API — singleton-loader. Vi använder den för
    onStateChange-events (cookie-lagring av play-paus-state över sessions).
    INTE för 360°-drag — det ger broken UX i iframe (känd YouTube-bug
    på mobile). 360°-drag erbjuds via "Switch to 360° viewer"-toggle som
    deep-link:ar till YouTube-appen/-webben istället.

    Loaded once per page-load, cached i window.__igelkottYTAPI-promise.
  */
  function loadYouTubeIframeAPI() {
    if (window.__igelkottYTAPI) return window.__igelkottYTAPI;
    window.__igelkottYTAPI = new Promise((resolve) => {
      if (window.YT && window.YT.Player) {
        resolve(window.YT);
        return;
      }
      // YouTube API anropar global onYouTubeIframeAPIReady när loaded.
      // Chain in case andra extensions också lyssnar.
      const prevHandler = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        if (typeof prevHandler === 'function') prevHandler();
        resolve(window.YT);
      };
      // Sektionen hämtar redan iframe_api (async, 2026-10-28). Laddas den
      // fortfarande räcker hanteraren ovan.
      if (window.YT && window.YT.loading) return;
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.async = true;
      document.head.appendChild(tag);
    });
    return window.__igelkottYTAPI;
  }

  // Pausminnet (24 h i localStorage) borttaget 2026-10-28: ett tryck i
  // viewern pausar, och då autospelade ingen plate på ett dygn (Eric:
  // "fixar du autoplay på youtube?"). Nu startar videon alltid ljudlöst.

  // ── "Switch to 360° viewer"-toggle ────────────────────────────────────
  // Default YouTube-iframe-embed har broken 360°-controls. Istället för
  // att hacka kring det med custom overlay (laggigt, fragmenterat UX),
  // bjuder vi användaren ut till YouTube-appen (mobile) eller -hemsidan
  // (desktop) där 360°-drag fungerar nativt.
  function buildYouTubeToggle(youtubeId) {
    /*
      rev215 (designkritik 2026-07-25, punkt 6): switch-toggeln såg ut
      som ett LÄGE ("vilket läge är jag i nu?") men handlingen är en
      GESTURE — den öppnar YouTube externt (app/web) där 360°-drag
      fungerar nativt. En segmented `Flat | 360°` (kritikens förslag)
      hade ljugit om att sidan har två inbäddade lägen. Ärlig fix:
      vanlig action-pill i samma stil som View similar, med extern-
      länk-ikon som signalerar att man lämnar sidan.

      (v1-toggeln med thumb-animation, Eric 2026-04-27, utgick här.)
    */
    const wrap = document.createElement('div');
    wrap.className = 'igelkott-product__yt-toggle-wrap';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'igelkott-product__similar igelkott-product__yt-open';
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<circle cx="12" cy="12" r="9"></circle>' +
      '<path d="M3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18"></path>' +
      '</svg>' +
      '<span>360° on YouTube</span>' +
      '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"></path>' +
      '</svg>';

    btn.addEventListener('click', () => {
      log.info('product.yt_toggle_clicked', { youtube_id: youtubeId });
      openInYouTube(youtubeId);
    });

    wrap.appendChild(btn);
    return wrap;
  }

  function openInYouTube(videoId) {
    const ua = navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    if (isAndroid || isIOS) {
      // Försök öppna native app via deep-link. Om appen är installerad
      // tar den över sidan (document.hidden=true) och vi rensar timern.
      // Om timern hinner trigga = appen saknas → visa install-prompt
      // ISTÄLLET för att fall tillbaka till web. Anledning: gyro-control
      // (head-tracking på 360°-video) fungerar bara i native YouTube-appen,
      // så web-fallback ger en sämre upplevelse än produkten lovar.
      const appUrl = isAndroid
        ? `vnd.youtube://watch?v=${videoId}`
        : `youtube://watch?v=${videoId}`;

      const fallbackTimer = setTimeout(() => {
        showYouTubeInstallPrompt(isAndroid, videoId);
      }, 1500);

      const onVisChange = () => {
        if (document.hidden) clearTimeout(fallbackTimer);
      };
      document.addEventListener('visibilitychange', onVisChange, { once: true });

      window.location.href = appUrl;
    } else {
      window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank', 'noopener');
    }
  }

  /*
    Install-prompt-dialog. Visas när YouTube-app-deep-link timeout:ar
    (= appen saknas). Erbjuder store-länk + "continue in browser"-fallback
    så användaren kan välja. Mountas direkt på body som en overlay.
  */
  function showYouTubeInstallPrompt(isAndroid, videoId) {
    log.info('product.yt_install_prompt_shown', {
      platform: isAndroid ? 'android' : 'ios',
      youtube_id: videoId,
    });

    // Återställ toggle-state om den fortfarande är .is-active
    const activeToggle = document.querySelector('.igelkott-product__yt-toggle.is-active');
    if (activeToggle) activeToggle.classList.remove('is-active');

    const storeUrl = isAndroid
      ? 'https://play.google.com/store/apps/details?id=com.google.android.youtube'
      : 'https://apps.apple.com/app/youtube/id544007664';
    const storeLabel = isAndroid ? 'Open in Play Store' : 'Open in App Store';
    const webFallback = `https://m.youtube.com/watch?v=${videoId}`;

    const overlay = document.createElement('div');
    overlay.className = 'igelkott-yt-install';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', 'Install YouTube');
    overlay.innerHTML = `
      <div class="igelkott-yt-install__backdrop" data-yt-install-backdrop></div>
      <div class="igelkott-yt-install__card">
        <h2 class="igelkott-yt-install__title">Install YouTube for full 360°</h2>
        <p class="igelkott-yt-install__text">
          Head-tracking and gyro controls only work in the YouTube app.
          Install it for the full 360° experience.
        </p>
        <div class="igelkott-yt-install__actions">
          <a class="igelkott-yt-install__btn igelkott-yt-install__btn--primary"
             href="${storeUrl}" target="_blank" rel="noopener">
            ${storeLabel}
          </a>
          <a class="igelkott-yt-install__btn igelkott-yt-install__btn--ghost"
             href="${webFallback}" target="_blank" rel="noopener">
            Continue in browser
          </a>
        </div>
        <button type="button" class="igelkott-yt-install__close"
                data-yt-install-close aria-label="Close">×</button>
      </div>
    `;
    document.body.appendChild(overlay);

    function dismiss() {
      overlay.remove();
      log.info('product.yt_install_prompt_dismissed');
    }
    overlay.querySelector('[data-yt-install-backdrop]').addEventListener('click', dismiss);
    overlay.querySelector('[data-yt-install-close]').addEventListener('click', dismiss);
    document.addEventListener('keydown', function escHandler(ev) {
      if (ev.key === 'Escape') {
        dismiss();
        document.removeEventListener('keydown', escHandler);
      }
    });
  }

  // [Borttaget 2026-04-27] `attachSphericalDrag` — custom drag-overlay
  // för 360°-pan i iframe. Gav broken UX (overlay-sizing-buggar,
  // click-blocking på play-button, cross-origin iframe ignorerar
  // synthetic-clicks). Ersatt av "Switch to 360° viewer"-toggle som
  // skickar användaren till YouTube-appen/-webben för riktig 360°.

  /*
    Eric 2026-10-28 — "lös problemet så det går att pana runt i youtube 360
    på hemsidan". Uppmätt i Chrome desktop: inbäddad spelare (både
    youtube.com och youtube-nocookie.com) renderar 360-projektionen men en
    drag pausar videon i stället för att vrida den (yaw står kvar på 0),
    medan setSphericalProperties vrider den. På mobil stödjer YouTube-
    embeds aldrig drag-pan. Därför egen drag → setSphericalProperties.

    Skillnad mot borttagna attachSphericalDrag (2026-04-27):
      - lagret ligger i viewern, inset från topp/botten så YouTubes
        titelrad och kontrollrad är klickbara;
      - klick utan rörelse = play/pause via API:t (blockerar inte play);
      - aktivt först när videon spelar OCH spelaren rapporterar sfäriska
        egenskaper — före första play, och för platta videor, är lagret
        genomskinligt för musen (pointer-events: none).
    Touch: riktningslås per drag — vågrätt vrider, lodrätt scrollar sidan
    (Eric 2026-10-28: "scroll ned ska fortfarande scrolla hemsidan").

    Rev 2 samma dag (Eric: "går inte att titta runt med mobil.. och med
    mobil ska det vara gyro"):
      - fasta 56 px fritt lämnade 80 av 192 px dragbara på mobil →
        marginalerna är nu andel av höjden (max 56 px);
      - dragriktningen var spegelvänd: uppmätt yaw +50 = vyn vrids åt
        vänster, pitch +40 = uppåt. Drag följer nu bilden ("ta tag i");
      - gyro på pekskärm: deviceorientation i toppsidan → yaw/pitch.
        Android startar direkt; iOS kräver en tryckning
        (DeviceOrientationEvent.requestPermission), därav knappen.
  */
  /*
    Eric 2026-11-03 ("fixa så helskärm får en 360 mask"): YouTubes egen knapp
    sätter bara iframen i helskärm, och då hamnar draglagret utanför. Spelaren
    har därför fs=0, och den här knappen sätter hela behållaren i helskärm.
    Platta spelare (iPhone/iPad-UA) kommer aldrig hit: där ligger stillbilden
    överst och videon öppnas i YouTube-appen.
  */
  function addFullscreenButton(target) {
    const enter = target.requestFullscreen || target.webkitRequestFullscreen;
    if (!enter) return;
    const current = () => document.fullscreenElement || document.webkitFullscreenElement;
    const leave = () => (document.exitFullscreen || document.webkitExitFullscreen).call(document);
    const ICON_ENTER = '<path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3"></path>';
    const ICON_LEAVE = '<path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3"></path>';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'igelkott-product__fullscreen';
    const sync = () => {
      const on = current() === target;
      btn.setAttribute('aria-label', on ? 'Exit full screen' : 'Full screen');
      btn.innerHTML = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${on ? ICON_LEAVE : ICON_ENTER}</svg>`;
    };
    btn.addEventListener('click', () => {
      if (current() === target) leave();
      else enter.call(target);
    });
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    sync();
    target.appendChild(btn);
  }

  function setupSphericalPan(target, player) {
    if (!target || !player || typeof player.setSphericalProperties !== 'function') return;
    if (target.querySelector('.igelkott-product__pan-layer')) return;
    const layer = document.createElement('div');
    layer.className = 'igelkott-product__pan-layer';
    // display:block — temats `div:empty { display: none }` döljer annars lagret.
    layer.style.cssText = 'display:block;position:absolute;left:0;right:0;' +
      // Botten 132 px / 30 %: lagret ska sluta ovanför YouTubes förstora-
      // knapp, som ligger ovanför förloppslisten och raden "More videos"
      // (Eric 2026-11-03; 100 px / 24 % täckte knappen).
      'top:min(56px,14%);bottom:min(132px,30%);' +
      // touch-action:pan-y — lodrätt är sidscroll; vågrätt låses i
    // touchmove nedan så att även lite sneda drag blir vridning.
    'z-index:2;pointer-events:none;cursor:grab;touch-action:pan-y;';
    target.appendChild(layer);
    addFullscreenButton(target);

    // maxTouchPoints: en iPad med tangentbord/trackpad rapporterar pointer:
    // fine men har gyro och pekskärm (Eric 2026-10-28, iPad: finger ok,
    // gyro inte).
    const coarse = (navigator.maxTouchPoints || 0) > 0 ||
      (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    const spherical = () => {
      try {
        const sp = player.getSphericalProperties && player.getSphericalProperties();
        return sp && typeof sp.yaw === 'number' ? sp : null;
      } catch (_) { return null; }
    };
    const handle = target.closest('[data-handle]')?.dataset.handle;

    let pending = null;
    let raf = 0;
    const flush = () => {
      raf = 0;
      // enableOrientationSensor:false — annars ignorerar YouTubes mobil-
      // spelare yaw/pitch från API:t (uppmätt på Galaxy Fold3, Chrome 156:
      // setSphericalProperties({yaw:60}) gav yaw 0 tills flaggan sattes).
      if (pending) { player.setSphericalProperties({ ...pending, enableOrientationSensor: false }); pending = null; }
    };
    const apply = (props) => {
      pending = props;
      if (!raf) raf = requestAnimationFrame(flush);
    };
    const wrap = (deg) => ((deg % 360) + 360) % 360;

    // ── Gyro ──────────────────────────────────────────────────────────
    // Kamerans blickriktning = telefonens baksida (−Z) i jordkoordinater
    // (X öst, Y nord, Z upp), W3C-matrisen R = Rz(α)·Rx(β)·Ry(γ). Den
    // beror inte på skärmorienteringen, så stående och liggande funkar
    // utan omräkning. Yaw räknas relativt läget när gyrot startade.
    let gyro = null; // { yaw0, devYaw0, offset }
    // Fold3/Chrome 156 skickar bara `deviceorientationabsolute`, aldrig
    // `deviceorientation`. Lyssna på båda och lås på den som först ger
    // värden — blandas de hoppar yaw mellan relativ och kompass-alpha.
    const ORIENT_EVENTS = ['deviceorientation', 'deviceorientationabsolute'];
    let orientType = null;
    const onOrientation = (ev) => {
      if (!gyro || ev.alpha == null || ev.beta == null || ev.gamma == null) return;
      if (!orientType) orientType = ev.type;
      if (ev.type !== orientType) return;
      const d = Math.PI / 180;
      const sA = Math.sin(ev.alpha * d), cA = Math.cos(ev.alpha * d);
      const sB = Math.sin(ev.beta * d), cB = Math.cos(ev.beta * d);
      const sG = Math.sin(ev.gamma * d), cG = Math.cos(ev.gamma * d);
      const fx = -(cG * sA * sB + cA * sG);
      const fy = -(sA * sG - cA * cG * sB);
      const fz = -cB * cG;
      // Moturs kompassvinkel = YouTubes yaw-riktning (+ = vänster).
      const devYaw = -Math.atan2(fx, fy) / d;
      const devPitch = Math.asin(Math.max(-1, Math.min(1, fz))) / d;
      if (!gyro.devYaw0Set) {
        // Ligger telefonen platt finns ingen meningsfull kompassriktning
        // (blicken pekar rakt ner) — uppmätt på Fold3: start på bordet gav
        // yaw 270 / pitch 80 när den lyftes. Vänta tills den är lyft.
        if (Math.abs(devPitch) > 60) return;
        gyro.devYaw0 = devYaw;
        gyro.devYaw0Set = true;
      }
      // Pitch absolut, som YouTube-appen: lodrät telefon = horisonten.
      apply({ yaw: wrap(gyro.yaw0 + devYaw - gyro.devYaw0 + gyro.offset), pitch: devPitch, roll: 0 });
    };
    const startGyro = () => {
      if (gyro) return;
      const sp = spherical();
      if (!sp) return;
      gyro = { yaw0: sp.yaw, devYaw0: 0, devYaw0Set: false, offset: 0 };
      ORIENT_EVENTS.forEach((t) => window.addEventListener(t, onOrientation));
      log.info('product.spherical_gyro_started', { handle });
    };
    let gyroBtn = null;
    const offerGyro = () => {
      if (!coarse || typeof window.DeviceOrientationEvent === 'undefined') return;
      const needsPermission = typeof DeviceOrientationEvent.requestPermission === 'function';
      if (!needsPermission) { startGyro(); return; }
      // Kommer sensorvärden ändå (Chrome/Android) startar gyrot direkt;
      // iOS skickar inget förrän tillståndet getts via knappen.
      const probe = (ev) => {
        if (ev.alpha == null) return;
        ORIENT_EVENTS.forEach((t) => window.removeEventListener(t, probe));
        if (gyroBtn) { gyroBtn.remove(); gyroBtn = null; }
        startGyro();
        onOrientation(ev);
      };
      ORIENT_EVENTS.forEach((t) => window.addEventListener(t, probe));
      // Chrome har också requestPermission men svarar "granted" utan
      // tryckning; iOS nekar utanför en användargest → då knappen.
      DeviceOrientationEvent.requestPermission()
        .then((state) => { if (state === 'granted') startGyro(); else showGyroButton(); })
        .catch(showGyroButton);
      // Fold3/Chrome 156: anropet utan gest varken löstes eller nekades.
      setTimeout(() => { if (!gyro) showGyroButton(); }, 1500);
    };
    const showGyroButton = () => {
      if (gyro || gyroBtn) return;
      gyroBtn = document.createElement('button');
      gyroBtn.type = 'button';
      gyroBtn.className = 'igelkott-product__gyro-btn';
      gyroBtn.textContent = 'Move phone to look around';
      gyroBtn.style.cssText = 'position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);' +
        'padding:8px 14px;border-radius:999px;border:0;font:inherit;font-size:13px;' +
        'background:rgba(0,0,0,.65);color:#fff;cursor:pointer;';
      gyroBtn.addEventListener('pointerdown', (ev) => ev.stopPropagation());
      gyroBtn.addEventListener('pointerup', (ev) => ev.stopPropagation());
      gyroBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        askGyroPermission();
      });
      layer.appendChild(gyroBtn);
      // iOS: första beröringen av videon (tryck eller drag) räcker som
      // användargest — man behöver inte träffa knappen.
      layer.addEventListener('touchend', askGyroPermission);
    };
    let asking = false;
    const askGyroPermission = () => {
      if (gyro || asking) return;
      asking = true;
      layer.removeEventListener('touchend', askGyroPermission);
      DeviceOrientationEvent.requestPermission().then((state) => {
        if (gyroBtn) { gyroBtn.remove(); gyroBtn = null; }
        log.info('product.spherical_gyro_permission', { handle, state });
        if (state === 'granted') startGyro();
      }).catch((err) => {
        // Ingen giltig gest (t.ex. touchend efter ett drag) → knappen kvar.
        asking = false;
        layer.addEventListener('touchend', askGyroPermission);
        log.info('product.spherical_gyro_failed', { handle, error: err.message });
      });
    };

    let active = false;
    const activate = () => {
      if (active || !spherical()) return;
      active = true;
      layer.style.pointerEvents = 'auto';
      log.info('product.spherical_pan_enabled', { handle, coarse });
      offerGyro();
    };
    player.addEventListener('onStateChange', (ev) => { if (ev.data === 1) activate(); });

    // ── Drag ──────────────────────────────────────────────────────────
    let drag = null;
    layer.addEventListener('pointerdown', (ev) => {
      if (ev.button !== 0) return;
      const sp = spherical();
      if (!sp) return;
      drag = { id: ev.pointerId, x: ev.clientX, y: ev.clientY, yaw: sp.yaw, pitch: sp.pitch,
        fov: sp.fov || 100, moved: false, touch: ev.pointerType !== 'mouse',
        offset: gyro ? gyro.offset : 0 };
      try { layer.setPointerCapture(ev.pointerId); } catch (_) { /* draget funkar utan capture */ }
    });
    layer.addEventListener('pointermove', (ev) => {
      if (!drag || ev.pointerId !== drag.id) return;
      const dx = ev.clientX - drag.x;
      const dy = ev.clientY - drag.y;
      if (!drag.moved && Math.hypot(dx, dy) < 5) return;
      if (!drag.moved) { drag.moved = true; layer.style.cursor = 'grabbing'; }
      // Grader per pixel ≈ synfält / bredd: bilden följer pekaren.
      const k = drag.fov / (target.clientWidth || 1);
      if (gyro) {
        // Gyrot äger pitch; fingret förskjuter bara yaw.
        gyro.offset = drag.offset + dx * k;
        return;
      }
      const yaw = wrap(drag.yaw + dx * k);
      // Touch: bara vågrätt (lodrätt är sidscroll).
      const pitch = drag.touch ? drag.pitch : Math.max(-90, Math.min(90, drag.pitch + dy * k));
      apply({ yaw, pitch, roll: 0, fov: drag.fov });
    });
    const end = (ev) => {
      if (!drag || ev.pointerId !== drag.id) return;
      const wasDrag = drag.moved;
      drag = null;
      layer.style.cursor = 'grab';
      if (wasDrag || ev.type === 'pointercancel') return;
      // Trycket gick till gyrofrågan (iOS, knappen syns) — pausa inte.
      if (gyroBtn) return;
      try {
        if (player.getPlayerState() === 1) player.pauseVideo(); else player.playVideo();
      } catch (_) { /* spelaren borta */ }
    };
    // Riktningslås: första rörelsen över 8 px avgör. Vågrätt → stoppa
    // sidscrollen för resten av draget; lodrätt → låt webbläsaren scrolla
    // (den skickar då pointercancel och draget ovan avbryts).
    let touchStart = null;
    let lock = null;
    layer.addEventListener('touchstart', (ev) => {
      const t = ev.touches[0];
      touchStart = t ? { x: t.clientX, y: t.clientY } : null;
      lock = null;
    }, { passive: true });
    layer.addEventListener('touchmove', (ev) => {
      const t = ev.touches[0];
      if (!touchStart || !t) return;
      if (!lock) {
        const dx = t.clientX - touchStart.x;
        const dy = t.clientY - touchStart.y;
        if (Math.hypot(dx, dy) < 8) return;
        lock = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
      }
      if (lock === 'h' && ev.cancelable) ev.preventDefault();
    }, { passive: false });
    layer.addEventListener('pointerup', end);
    layer.addEventListener('pointercancel', end);
  }

  // rev213 — hero-YT-player-instans, sätts i setupVideoHero, konsumeras
  // av setupTrimStrip via igelkott-product-player-ready-eventet.
  let heroPlayerInstance = null;

  /*
    rev213 — Trim-strip (Eric 2026-07-20).

    Kund markerar in/out-punkter på produktvideon och sparar rangen till
    en collection. Tvingande koppling: trim persisteras BARA via
    collections-popupen (openCollectionPopup(handle, {trim})) — en trim
    kan inte existera utanför en collection. Olika collections kan hålla
    olika trims för samma plate (per-(collection,handle)-modell, se
    igelkott-collections.js setPlateTrim).

    Påverkar INTE köpflödet — ren konto-funktion.

    UI följer NLE-konventionen från _atlas/knowledge/web/nle-in-out-markers.md
    (IgelkottDailies-mönstret): cool cyan playhead/progress, varma guld-
    brackets [ ] med I/O-labels + region-tint. Osparad trim ligger kvar i
    localStorage per handle (pending) tills den sparas eller rensas.

    YT IFrame API ger getCurrentTime/getDuration/seekTo — playern är
    redan API-styrd sedan rev134 (play-state-cookie), ingen embed-ändring.
  */
  const TRIM_PENDING_KEY_PREFIX = 'igelkott.trim.pending.';
  const TRIM_MIN_GAP_S = 0.5;

  function setupTrimStrip(root) {
    const mountTarget = root.querySelector('[data-product-viewer-mount]');
    const handle = root.dataset.handle || '';
    if (!mountTarget || !handle) return;

    const strip = document.createElement('div');
    strip.className = 'igelkott-trim';
    strip.setAttribute('data-igelkott-trim', '');
    strip.hidden = true; // visas vid player-ready (duration krävs)
    strip.innerHTML =
      '<div class="igelkott-trim__bar" data-trim-bar>' +
      '  <div class="igelkott-trim__range" data-trim-range></div>' +
      '  <div class="igelkott-trim__progress" data-trim-progress></div>' +
      '  <div class="igelkott-trim__tick" data-trim-tick-in></div>' +
      '  <div class="igelkott-trim__tick" data-trim-tick-out></div>' +
      '  <div class="igelkott-trim__thumb" data-trim-thumb></div>' +
      '</div>' +
      '<div class="igelkott-trim__controls">' +
      // rev217 (Eric) — "play in to out": seek:ar till in-punkten och
      // spelar; range-locken (rev213b) håller sen loopen inom rangen.
      // NLE-konvention: knappen startar ALLTID om från in.
      '  <button type="button" class="igelkott-trim__btn igelkott-trim__play" data-trim-play disabled aria-label="Play in to out">' +
      '    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"></path></svg>' +
      '  </button>' +
      '  <button type="button" class="igelkott-trim__btn" data-trim-set-in>Set in</button>' +
      '  <button type="button" class="igelkott-trim__btn" data-trim-set-out>Set out</button>' +
      '  <span class="igelkott-trim__timecode" data-trim-timecode></span>' +
      '  <button type="button" class="igelkott-trim__btn igelkott-trim__btn--ghost" data-trim-clear hidden>Clear</button>' +
      // rev223 (Eric) — dela klippet + rangen UTAN konto. Rangen kodas i
      // URL:en (?trim=in-out) så länken är stateless: ingen token, ingen
      // lagring, ingen inloggning i någondera änden.
      '  <button type="button" class="igelkott-trim__btn igelkott-trim__share" data-trim-share disabled>' +
      '    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '      <circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle>' +
      '      <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4"></path>' +
      '    </svg>' +
      '    <span data-trim-share-label>Share range</span>' +
      '  </button>' +
      '  <button type="button" class="igelkott-trim__save" data-trim-save disabled>Save trim to collection</button>' +
      '</div>';
    /*
      rev215 (designkritik 2026-07-25, punkt 1+2): panelen låg direkt
      under spelaren och tryckte ner köp-innehållet, samtidigt som
      entry-knappen satt i actions-raden NEDANFÖR sin egen panel
      ("två parallella system"). Nu: panelen infogas EFTER hero-actions-
      raden → knappen sitter ovanför panelen den öppnar, och collapsed-
      default (aldrig auto-expand, se activate) håller köpvägen kort.
    */
    const actionsRow = root.querySelector('[data-product-hero-actions]');
    (actionsRow || mountTarget).insertAdjacentElement('afterend', strip);

    const barEl = strip.querySelector('[data-trim-bar]');
    const rangeEl = strip.querySelector('[data-trim-range]');
    const progressEl = strip.querySelector('[data-trim-progress]');
    const tickInEl = strip.querySelector('[data-trim-tick-in]');
    const tickOutEl = strip.querySelector('[data-trim-tick-out]');
    const thumbEl = strip.querySelector('[data-trim-thumb]');

    /*
      rev216b (Eric: "jag trycker men ingen prick dyker upp") — Dawn:s
      base.css:471 har `div:empty { display: none }` som äter ALLA tomma
      divar, och `div:empty` (0,1,1) vinner i specificitet över vår
      klass-selector (0,1,0). Thumb + progress är alltid tomma divar →
      de har ALDRIG renderats (ticks/range överlevde bara för att
      render() ger dem inline display). Inline-style vinner över allt —
      sätt det vid mount, samma mönster som ticks.
    */
    progressEl.style.display = 'block';
    thumbEl.style.display = 'block';
    const playBtn = strip.querySelector('[data-trim-play]');
    const setInBtn = strip.querySelector('[data-trim-set-in]');
    const setOutBtn = strip.querySelector('[data-trim-set-out]');
    const timecodeEl = strip.querySelector('[data-trim-timecode]');
    const clearBtn = strip.querySelector('[data-trim-clear]');
    const shareBtn = strip.querySelector('[data-trim-share]');
    const saveBtn = strip.querySelector('[data-trim-save]');

    let player = null;
    let duration = 0;
    let inS = null;
    let outS = null;
    let pollTimer = null;
    /*
      rev216 (Eric 2026-07-25): "när man trycker på vår tidslinje när den
      är tom ska en blå prick dyka upp, nu markerar den men ingen visuell
      feedback." seekTo() är async i YT-API:t (extra långsam i cued-state
      före första play) — getCurrentTime() hänger kvar på gamla värdet i
      upp till ett par sekunder. Optimistisk UI: klick flyttar pricken
      OMEDELBART till klickpositionen; poll:en visar seek-målet tills
      spelaren kommit ikapp (±0.75s) eller 2.5s passerat.
    */
    let seekTargetS = null;
    let seekTargetAt = 0;

    function fmt(s) {
      if (s == null || !isFinite(s)) return '–:––';
      const m = Math.floor(s / 60);
      const sec = Math.floor(s % 60);
      return m + ':' + String(sec).padStart(2, '0');
    }

    /*
      rev223 — läs `?trim=<in>-<out>` ur URL:en. HÖGST prioritet i
      hydrate-kedjan: en delad länk är ett explicit uttryck för vad
      mottagaren ska se, och ska slå både egen pending-range och sparade
      collection-trims. Clampas mot duration i activate().
    */

    function loadFromUrl() {
      try {
        const raw = new URLSearchParams(window.location.search).get('trim');
        if (!raw) return false;
        const m = raw.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/);
        if (!m) return false;
        const a = parseFloat(m[1]);
        const b = parseFloat(m[2]);
        if (!isFinite(a) || !isFinite(b) || b <= a) return false;
        inS = a;
        outS = b;
        log.info('product.trim_from_url', { handle, in_s: a, out_s: b });
        return true;
      } catch (_) {
        return false;
      }
    }

    function loadPending() {
      if (loadFromUrl()) return true;
      try {
        const raw = localStorage.getItem(TRIM_PENDING_KEY_PREFIX + handle);
        if (raw) {
          const t = JSON.parse(raw);
          if (t && typeof t.in_s === 'number' && typeof t.out_s === 'number' && t.out_s > t.in_s) {
            inS = t.in_s;
            outS = t.out_s;
            return true;
          }
        }
      } catch (_) {}
      return hydrateFromCollections();
    }

    /*
      rev213c (Eric 2026-07-24): "när jag backade ut, navigera till
      collection och gick in på den igen så var markeringen borta."

      Rotorsak: v1 rensade pending-nyckeln efter lyckad save, och
      strippen hydratiserade BARA från pending → nästa sidbesök = tom
      strip trots sparad trim. Fix i två delar:
        1. Pending rensas aldrig längre (den är "arbetsrangen" på
           strippen, save är en kopia in i collection:en)
        2. Fallback här: saknas pending (annan enhet, rensad storage),
           hämta trimmen från den SENAST UPPDATERADE collection:en som
           har en trim för den här platen. Per-collection-modellen gör
           att flera ranges kan finnas — senast-ändrad är den mest
           förväntade att se igen.
    */
    function hydrateFromCollections() {
      const api = window.igelkott && window.igelkott.collections;
      if (!api || typeof api.getCollections !== 'function') return false;
      let best = null;
      let bestUpdated = '';
      try {
        api.getCollections().forEach((c) => {
          const t = c && c.trims && c.trims[handle];
          if (t && typeof t.in_s === 'number' && typeof t.out_s === 'number'
              && t.out_s > t.in_s && (c.updated_at || '') >= bestUpdated) {
            best = t;
            bestUpdated = c.updated_at || '';
          }
        });
      } catch (_) { return false; }
      if (best) {
        inS = best.in_s;
        outS = best.out_s;
        log.info('product.trim_hydrated_from_collection', {
          handle, in_s: inS, out_s: outS,
        });
        return true;
      }
      /*
        rev214 — tredje hydratiseringskälla: aktiv shared-kontext.
        Anonym besökare som kommit via en delad collection (?shared=
        på feeden → sessionStorage-ctx) ser ägarens markerade segment
        på produktsidan: marks + range-lock-loop, exakt som ägaren
        satte dem. Egna collections vinner över shared (raden ovan) —
        shared är default-förslag, inte override.
      */
      if (typeof api.getSharedTrim === 'function') {
        const st = api.getSharedTrim(handle);
        if (st) {
          inS = st.in_s;
          outS = st.out_s;
          log.info('product.trim_hydrated_from_shared', {
            handle, in_s: inS, out_s: outS,
          });
          return true;
        }
      }
      return false;
    }

    function savePending() {
      try {
        if (inS != null && outS != null) {
          localStorage.setItem(
            TRIM_PENDING_KEY_PREFIX + handle,
            JSON.stringify({ in_s: inS, out_s: outS })
          );
        } else {
          localStorage.removeItem(TRIM_PENDING_KEY_PREFIX + handle);
        }
      } catch (_) {}
    }

    function render() {
      if (!duration) return;
      // KRITISKT (atlas-gotcha #1): explicit 'block', aldrig '' — CSS-
      // default är display:none och '' faller tillbaka dit.
      if (inS != null) {
        tickInEl.style.left = (inS / duration) * 100 + '%';
        tickInEl.style.display = 'block';
      } else {
        tickInEl.style.display = 'none';
      }
      if (outS != null) {
        tickOutEl.style.left = (outS / duration) * 100 + '%';
        tickOutEl.style.display = 'block';
      } else {
        tickOutEl.style.display = 'none';
      }
      if (inS != null && outS != null) {
        rangeEl.style.left = (inS / duration) * 100 + '%';
        rangeEl.style.width = ((outS - inS) / duration) * 100 + '%';
        rangeEl.style.display = 'block';
      } else {
        rangeEl.style.display = 'none';
      }
      const both = inS != null && outS != null;
      saveBtn.disabled = !both;
      if (playBtn) playBtn.disabled = !both;
      if (shareBtn) shareBtn.disabled = !both;
      clearBtn.hidden = inS == null && outS == null;
      /*
        Frame.io-stil range-chip (redesign 2026-07-24): "2:39 – 6:14 · 3:34".
        Ingen text alls innan första marken satts (:empty-CSS gömmer chippen)
        — mindre visuellt brus än v1:s "I –:–– · O –:––"-placeholder.
      */
      if (inS == null && outS == null) {
        timecodeEl.textContent = '';
      } else if (!both) {
        timecodeEl.textContent = fmt(inS != null ? inS : outS) + ' – …';
      } else {
        timecodeEl.textContent = fmt(inS) + ' – ' + fmt(outS) + ' · ' + fmt(outS - inS);
      }
    }

    function updatePlayhead() {
      if (!player || !duration) return;
      let t = 0;
      try { t = player.getCurrentTime() || 0; } catch (_) { return; }
      // rev216 — optimistisk seek-visning: visa klick-målet tills
      // spelaren kommit ikapp (annars "hoppar pricken tillbaka").
      if (seekTargetS != null) {
        if (Math.abs(t - seekTargetS) <= 0.75 || Date.now() - seekTargetAt > 2500) {
          seekTargetS = null;
        } else {
          t = seekTargetS;
        }
      }
      /*
        rev213b (Eric 2026-07-24): "när man spelar klippet i youtube måste
        den låsa till in-ut annars fungerar det inte som tänkt."

        Range-lock: när BÅDA marks är satta och videon SPELAR loopas
        playback inom [in, out] — passerar playhead out (eller hamnar
        före in, t.ex. via YT:s egen seek-bar) hoppar vi till in.
        0.35s för-in-tolerans så seekTo(in) själv inte re-triggar.
        Loop (inte pausa-vid-out): kunden utvärderar sitt segment
        repetitivt, samma sätt som plates loopas på LED-walls.
        Lås släpps via Clear eller genom att dra handles.
        Enforcement bara i PLAYING-state (1) — pausad scrubbing utanför
        rangen ska inte fightas; låset tar vid play igen.
      */
      if (inS != null && outS != null && !dragging) {
        let state = -1;
        try { state = player.getPlayerState(); } catch (_) {}
        if (state === 1 && (t > outS || t < inS - 0.35)) {
          try { player.seekTo(inS, true); } catch (_) {}
          t = inS;
        }
      }
      const pct = Math.max(0, Math.min(100, (t / duration) * 100));
      progressEl.style.width = pct + '%';
      thumbEl.style.left = pct + '%';
    }

    function startPolling() {
      if (pollTimer) return;
      // 250ms räcker för en 4px-bar — rAF vore slöseri (rev208-lärdomen:
      // homepage-frame-budgeten är dyrbar, produktsidan likaså).
      pollTimer = setInterval(updatePlayhead, 250);
    }

    function timeFromEvent(ev) {
      const rect = barEl.getBoundingClientRect();
      const x = Math.max(rect.left, Math.min(rect.right, ev.clientX));
      return ((x - rect.left) / rect.width) * duration;
    }

    function setIn(t) {
      inS = Math.max(0, Math.min(t, duration));
      // In efter befintlig Out → Out måste sättas om (NLE-logik: In vinner)
      if (outS != null && outS <= inS + TRIM_MIN_GAP_S) outS = null;
      savePending();
      render();
      log.info('product.trim_in_set', { handle, in_s: inS });
    }

    function setOut(t) {
      const candidate = Math.max(0, Math.min(t, duration));
      if (inS != null && candidate <= inS + TRIM_MIN_GAP_S) {
        // Out före In är ogiltigt — flasha timecode som feedback
        timecodeEl.classList.add('igelkott-trim__timecode--error');
        setTimeout(() => timecodeEl.classList.remove('igelkott-trim__timecode--error'), 600);
        return;
      }
      outS = candidate;
      savePending();
      render();
      log.info('product.trim_out_set', { handle, out_s: outS });
    }

    // ── Interaktioner ──────────────────────────────────────────────────
    /*
      rev223 (Eric 2026-07-30): "jag vill ha en share knapp som fungerar
      när man inte är inloggad som delar klippet med in/out"

      Rangen kodas i URL:en som `?trim=<in>-<out>` i hela sekunder:

          /products/<handle>?trim=38-63

      Stateless med flit — motsatsen till collection-delningen (rev214)
      som kräver token, serverlagring och inloggning för att SKAPA
      länken. Här behövs inget konto i någondera änden: avsändaren
      kopierar, mottagaren öppnar och ser exakt samma range med
      loop-uppspelning. Länken är dessutom bokmärkbar och överlever
      att delas vidare.

      Hela sekunder räcker för en delning och håller URL:en läsbar.
    */
    function buildShareUrl() {
      if (inS == null || outS == null) return null;
      const u = new URL(window.location.href);
      // Rensa params som inte hör till en delad range.
      u.searchParams.delete('shared');
      u.searchParams.delete('collection');
      u.searchParams.set('trim', Math.round(inS) + '-' + Math.round(outS));
      return u.origin + u.pathname + u.search;
    }

    function flashShare(ok, label) {
      const lbl = shareBtn.querySelector('[data-trim-share-label]');
      if (!lbl) return;
      const original = 'Share range';
      lbl.textContent = label || (ok ? 'Link copied' : 'Copy failed');
      shareBtn.classList.toggle('is-copied', !!ok);
      shareBtn.classList.toggle('is-err', !ok);
      setTimeout(() => {
        lbl.textContent = original;
        shareBtn.classList.remove('is-copied', 'is-err');
      }, 1800);
    }

    shareBtn.addEventListener('click', async () => {
      const url = buildShareUrl();
      if (!url) return;
      log.info('product.trim_share_clicked', {
        handle, in_s: Math.round(inS), out_s: Math.round(outS),
      });
      const how = await shareLink(url);
      if (how === 'shared') flashShare(true, 'Shared');
      else flashShare(how === 'copied');
    });
    // Share-knappen bredvid Like: delar rangen om den är satt, annars klippet
    root.__igkTrimShareUrl = buildShareUrl;

    playBtn.addEventListener('click', () => {
      if (inS == null || outS == null || !player) return;
      // Optimistisk prick-flytt till in-punkten (samma mönster som
      // bar-klick, rev216) + starta uppspelning. Range-locken tar
      // över loopningen därifrån.
      seekTargetS = inS;
      seekTargetAt = Date.now();
      const pct = Math.max(0, Math.min(100, (inS / duration) * 100));
      progressEl.style.width = pct + '%';
      thumbEl.style.left = pct + '%';
      thumbEl.classList.add('is-seeking');
      setTimeout(() => thumbEl.classList.remove('is-seeking'), 350);
      try {
        player.seekTo(inS, true);
        player.playVideo();
      } catch (_) {}
      log.info('product.trim_play_in_to_out', { handle, in_s: inS, out_s: outS });
    });

    setInBtn.addEventListener('click', () => {
      try { setIn(player.getCurrentTime() || 0); } catch (_) {}
    });
    setOutBtn.addEventListener('click', () => {
      try { setOut(player.getCurrentTime() || 0); } catch (_) {}
    });
    clearBtn.addEventListener('click', () => {
      inS = null;
      outS = null;
      savePending();
      render();
      log.info('product.trim_cleared', { handle });
    });

    saveBtn.addEventListener('click', () => {
      if (inS == null || outS == null) return;
      const api = window.igelkott && window.igelkott.collections;
      if (!api || typeof api.openCollectionPopup !== 'function') {
        log.warn('product.trim_save_no_collections_api', { handle });
        return;
      }
      log.info('product.trim_save_clicked', { handle, in_s: inS, out_s: outS });
      // Tvingande flöde: trim persisteras först när en collection väljs
      // i popupen. Anonyma users redirectas till login av openPopup.
      api.openCollectionPopup(handle, { trim: { in_s: inS, out_s: outS } });
    });

    /*
      rev213c — pending rensas INTE längre vid save (buggen: nästa
      sidbesök tappade markeringen). Istället: lyssna på server-pull —
      om strippen är tom när collections-data landar async, hydratisera
      i efterhand (t.ex. första besöket på ny enhet där pull:n hinner
      efter player-ready).
    */
    document.addEventListener('igelkott-collections-changed', (ev) => {
      const d = ev.detail || {};
      if (d.reason !== 'server_pull') return;
      if (inS != null || outS != null) return;
      // rev215: sen hydratisering uppdaterar badge + lock, men fäller
      // INTE ut panelen (designkritik punkt 1 — köp-först-ordning)
      if (hydrateFromCollections()) render();
    });

    // Bar-klick = seek (scrub-feedback). Bracket-drag = justera in/out.
    let dragging = null; // 'in' | 'out' | null
    barEl.addEventListener('pointerdown', (ev) => {
      if (!duration) return;
      const t = timeFromEvent(ev);
      // Träffade vi ett bracket? 12px hit-tolerans i tidsled.
      const tol = (12 / barEl.getBoundingClientRect().width) * duration;
      if (inS != null && Math.abs(t - inS) < tol && (outS == null || Math.abs(t - inS) <= Math.abs(t - outS))) {
        dragging = 'in';
      } else if (outS != null && Math.abs(t - outS) < tol) {
        dragging = 'out';
      } else {
        dragging = null;
        // rev216 — omedelbar visuell feedback: flytta pricken till
        // klickpositionen INNAN seekTo (som är async i YT-API:t).
        seekTargetS = t;
        seekTargetAt = Date.now();
        const pct = Math.max(0, Math.min(100, (t / duration) * 100));
        progressEl.style.width = pct + '%';
        thumbEl.style.left = pct + '%';
        thumbEl.classList.add('is-seeking');
        setTimeout(() => thumbEl.classList.remove('is-seeking'), 350);
        try { player.seekTo(t, true); } catch (_) {}
        return;
      }
      barEl.setPointerCapture(ev.pointerId);
      ev.preventDefault();
    });
    barEl.addEventListener('pointermove', (ev) => {
      if (!dragging || !duration) return;
      const t = timeFromEvent(ev);
      if (dragging === 'in') {
        inS = Math.max(0, Math.min(t, (outS != null ? outS - TRIM_MIN_GAP_S : duration)));
      } else {
        outS = Math.min(duration, Math.max(t, (inS != null ? inS + TRIM_MIN_GAP_S : 0)));
      }
      render();
    });
    barEl.addEventListener('pointerup', (ev) => {
      if (dragging) {
        savePending();
        try { barEl.releasePointerCapture(ev.pointerId); } catch (_) {}
        log.info('product.trim_bracket_dragged', { handle, which: dragging, in_s: inS, out_s: outS });
      }
      dragging = null;
    });

    /*
      I/O-tangentbord enligt _atlas/knowledge/web/keyboard-shortcuts.md:
      e.code (fysisk tangent), skippa när fokus är i text-input, gate:a
      bort modifier-kombos (Cmd+I = kursiv etc. ska inte kidnappas).
    */
    document.addEventListener('keydown', (ev) => {
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
      const tag = (document.activeElement && document.activeElement.tagName) || '';
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag)) return;
      if (!player || !duration) return;
      if (ev.code === 'KeyI' || ev.code === 'KeyO') {
        if (ev.code === 'KeyI') {
          try { setIn(player.getCurrentTime() || 0); } catch (_) {}
        } else {
          try { setOut(player.getCurrentTime() || 0); } catch (_) {}
        }
      }
    });

    // ── Aktivering vid player-ready ────────────────────────────────────
    document.addEventListener('igelkott-product-player-ready', (ev) => {
      player = (ev.detail && ev.detail.player) || heroPlayerInstance;
      if (!player) return;
      try { duration = player.getDuration() || 0; } catch (_) { duration = 0; }
      if (!duration) {
        // Duration kan vara 0 tills metadata laddats — polla kort.
        let tries = 0;
        const durPoll = setInterval(() => {
          tries += 1;
          try { duration = player.getDuration() || 0; } catch (_) {}
          if (duration || tries > 20) {
            clearInterval(durPoll);
            if (duration) activate();
          }
        }, 500);
        return;
      }
      activate();
    });

    function activate() {
      const hasMarks = loadPending();
      /*
        rev223 — clampa mot faktisk duration. En delad länk kan komma
        från ett annat klipp, eller ha manipulerade värden i URL:en.
        Ogiltig range kastas hellre än att visa något felaktigt.
      */
      if (inS != null && outS != null && duration) {
        inS = Math.max(0, Math.min(inS, duration));
        outS = Math.max(0, Math.min(outS, duration));
        if (outS - inS < TRIM_MIN_GAP_S) {
          log.warn('product.trim_url_range_invalid', {
            handle, in_s: inS, out_s: outS, duration_s: duration,
          });
          inS = null;
          outS = null;
        }
      }
      render();
      startPolling();
      // Eric 2026-11-03: alltid framme, ingen knapp som fäller ut.
      strip.hidden = false;
      updatePlayhead();
      log.info('product.trim_strip_activated', {
        handle,
        duration_s: duration,
        has_marks: !!hasMarks,
      });
    }
  }

  /*
    YouTube levererar 360-videon platt (equi utan remap, inget drag) till
    webbläsare som säger att de är iPad/iPhone: Chrome på iPad, Safari på
    iPhone (Eric 2026-10-28, "i chrome på ipad så laddar den equi bilden
    utan remap"). Uppmätt i WebKit: iPad- och iPhone-UA ger
    getSphericalProperties() = {} och ingen canvas, Mac-UA ger yaw/pitch.
    Safari på iPad skickar Mac-UA som standard och får 360 — den träffas
    inte här. Sidan kan inte ändra vilken UA iframen skickar.
  */
  const FLAT_YOUTUBE_UA = /iPhone|iPad|iPod/i.test(navigator.userAgent || '');

  // Annan webbläsare än Safari på iPad: Safari skickar Mac-UA och får 360,
  // så knappen flyttar besökaren dit (Eric 2026-10-28). x-safari-https:
  // öppnar Safari från Chrome på iPadOS 26 — provat på Erics iPad. På iPhone
  // får även Safari platt video, så där hjälper knappen inte.
  const OTHER_IPAD_BROWSER = /iPad/.test(navigator.userAgent || '') &&
    /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|GSA\/|DuckDuckGo/.test(navigator.userAgent || '');

  function buildOpenInSafari(root) {
    const a = document.createElement('a');
    a.className = 'igelkott-product__similar igelkott-product__open-safari';
    a.href = 'x-safari-' + location.href;
    a.innerHTML =
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<circle cx="12" cy="12" r="9"></circle><path d="M15.5 8.5l-2 5-5 2 2-5z"></path></svg>' +
      '<span>Open in Safari for 360° video</span>';
    a.addEventListener('click', () => {
      log.info('product.open_in_safari_clicked', { handle: root.dataset.handle });
    });
    return a;
  }

  // Equi-stillbilden i PSV över den platta spelaren, plus knappen som
  // öppnar videon i YouTube-appen (där 360 fungerar). Spelaren ligger kvar
  // under så Set in/out-strippen har sin player.
  // Remsan täcker bara ±30° kring horisonten (6:1). Med fast synfält 50°
  // (maxFov = minFov) får blicken luta högst 30 − 25 = 5° innan svart syns
  // över eller under (Eric 2026-10-28, "fortfarande svart över och under").
  const STILL_HALF_HEIGHT = 30 * Math.PI / 180;
  const STILL_HALF_FOV = 25 * Math.PI / 180;
  function clampPitch(root, pitch) {
    const lim = Math.max(0, STILL_HALF_HEIGHT - STILL_HALF_FOV);
    return Math.max(-lim, Math.min(lim, pitch));
  }
  async function mountEquiStill(container, url) {
    try {
      const mod = await loadPSV();
      return new mod.Viewer({
        container,
        panorama: url,
        canvasBackground: '#000000',
        touchmoveTwoFingers: false,
        mousewheel: false,
        navbar: false,
        loadingImg: null,
        defaultZoomLvl: 30,
      });
    } catch (err) {
      log.warn('product.equi_still_failed', { error: err.message });
      return null;
    }
  }

  function clampStillPitch(root, viewer) {
    viewer.addEventListener('position-updated', ({ position }) => {
      const p = clampPitch(root, position.pitch);
      if (Math.abs(p - position.pitch) > 1e-4) viewer.rotate({ yaw: position.yaw, pitch: p });
    });
  }

  // Gyro på iOS-reserven. PSV:s gyro-plugin startas aldrig av mountViewer,
  // och när den startas läser den skärmvinkeln från window.orientation.
  // Chrome på iPad (iPadOS 26) svarar 0 även i liggande läge, så bilden
  // låg vriden 90° (uppmätt via webbinspektören 2026-10-28). Därför
  // samma blickriktningsmatte som YouTube-spelaren (setupSphericalPan):
  // den är oberoende av skärmorienteringen. Yaw går som delta från
  // nuvarande läge, så fingerdrag ligger kvar. Pitch är absolut.
  // iOS kräver en gest för requestPermission: knapp + första beröring.
  function offerStillGyro(root, still, viewer, clamp) {
    if (typeof window.DeviceOrientationEvent === 'undefined') return;
    const handle = root.dataset.handle;
    const d = Math.PI / 180;
    let lastYaw = null;
    let orientType = null;
    const onOrientation = (ev) => {
      if (ev.alpha == null || ev.beta == null || ev.gamma == null) return;
      if (!orientType) orientType = ev.type;
      if (ev.type !== orientType) return;
      const sA = Math.sin(ev.alpha * d), cA = Math.cos(ev.alpha * d);
      const sB = Math.sin(ev.beta * d), cB = Math.cos(ev.beta * d);
      const sG = Math.sin(ev.gamma * d), cG = Math.cos(ev.gamma * d);
      const fx = -(cG * sA * sB + cA * sG);
      const fy = -(sA * sG - cA * cG * sB);
      const fz = -cB * cG;
      const pitch = Math.asin(Math.max(-1, Math.min(1, fz)));
      // Platt liggande enhet: ingen meningsfull kompassriktning.
      if (Math.abs(pitch) > 60 * d) { lastYaw = null; return; }
      const devYaw = Math.atan2(fx, fy); // medurs = höger, som PSV:s yaw
      let dy = lastYaw == null ? 0 : devYaw - lastYaw;
      if (dy > Math.PI) dy -= 2 * Math.PI;
      if (dy < -Math.PI) dy += 2 * Math.PI;
      lastYaw = devYaw;
      const pos = viewer.getPosition();
      viewer.rotate({ yaw: pos.yaw + dy, pitch: clamp ? clampPitch(root, pitch) : pitch });
    };
    const start = () => {
      ['deviceorientation', 'deviceorientationabsolute'].forEach((t) => window.addEventListener(t, onOrientation));
      try { viewer.stopAutoRotate && viewer.stopAutoRotate(); } catch (_) { /* äldre PSV */ }
      log.info('product.still_gyro_started', { handle });
    };
    if (typeof DeviceOrientationEvent.requestPermission !== 'function') { start(); return; }
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'igelkott-product__gyro-btn';
    btn.textContent = 'Move phone to look around';
    btn.style.cssText = 'position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);z-index:10;' +
      'padding:8px 14px;border-radius:999px;border:0;font:inherit;font-size:13px;' +
      'background:rgba(0,0,0,.65);color:#fff;cursor:pointer;';
    let asking = false;
    const ask = () => {
      if (asking) return;
      asking = true;
      still.removeEventListener('touchend', ask);
      DeviceOrientationEvent.requestPermission().then((state) => {
        log.info('product.still_gyro_permission', { handle, state });
        btn.remove();
        if (state === 'granted') start();
      }).catch((err) => {
        // Ingen giltig gest (touchend efter ett drag) → försök igen.
        asking = false;
        still.addEventListener('touchend', ask);
        log.info('product.still_gyro_failed', { handle, error: err && err.message });
      });
    };
    btn.addEventListener('click', (ev) => { ev.stopPropagation(); ask(); });
    still.appendChild(btn);
    still.addEventListener('touchend', ask);
  }

  function mountStillFallback(root, target, player, youtubeId) {
    try { player.pauseVideo(); } catch (_) { /* inte startad */ }
    const still = document.createElement('div');
    still.className = 'igelkott-product__still';
    still.style.cssText = 'position:absolute;inset:0;z-index:3;background:#000;display:block;';
    target.appendChild(still);
    // Zoom 100 = minFov 50° vertikalt: strippen är 60° hög, så den fyller
    // 16:9-rutan utan svarta bågar över och under.
    // Hel sfär (metafältet igelkott.equi_still, 2:1) när Publisher laddat
    // upp den — då går det att titta upp och ner. Annars horisontremsan
    // med låst synfält och lutning.
    const equi = root.dataset.equiStill;
    const mounted = equi ? mountEquiStill(still, equi) : mountViewer(root, still, 100, 50);
    mounted.then((viewer) => {
      if (!viewer) {
        // PSV laddade inte: hellre den platta spelaren än en svart ruta.
        still.remove();
        log.warn('product.still_fallback_removed', { handle: root.dataset.handle });
        return;
      }
      if (!equi) clampStillPitch(root, viewer);
      offerStillGyro(root, still, viewer, !equi);
    });
    const actions = root.querySelector('[data-product-hero-actions]');
    if (actions) {
      actions.insertBefore(buildYouTubeToggle(youtubeId), actions.firstChild);
      if (OTHER_IPAD_BROWSER) actions.insertBefore(buildOpenInSafari(root), actions.firstChild);
    }
    log.info('product.flat_youtube_fallback', { handle: root.dataset.handle, youtube_id: youtubeId });
  }

  // Med spelaren i HTML:en (igelkott-product.liquid) är YT.Player redan
  // kopplad av inline-skriptet; här hängs bara våra händelser på. onReady
  // kan redan ha kommit, och körs då direkt.
  function createHeroPlayer(YT, early, playerId, opts) {
    const st = window.__igelkottEarlyYT;
    const player = early && st && st.attach && st.attach();
    if (!player) return new YT.Player(playerId, opts);
    st.onReady = opts.events.onReady;
    st.onError = opts.events.onError;
    if (st.ready) opts.events.onReady(st.ready);
    log.info('product.early_player', { late: !!st.late, ready_before_js: !!st.ready });
    return player;
  }

  function setupVideoHero(root, youtubeId) {
    const target = root.querySelector('[data-product-viewer-mount]');
    if (!target) return false;

    if (!youtubeId) {
      // Ingen video → låt PSV mounta som fallback med equi-strippen.
      return false;
    }

    target.style.aspectRatio = '16 / 9';
    target.style.height = 'auto';
    target.style.width = '100%';
    target.style.background = '#000';
    target.style.position = 'relative';
    // Spelaren finns redan i HTML:en när sektionen hittat id:t i body_html
    // (2026-10-28, se igelkott-product.liquid). Då kopplas YT.Player till
    // den iframen, och resten av mounten töms.
    const early = target.querySelector('iframe[data-yt-early]');
    if (early) {
      Array.from(target.children).forEach((c) => { if (c !== early) c.remove(); });
    } else {
      target.innerHTML = '';
    }

    /*
      Standard YouTube-iframe via YT.Player constructor.

      Eric 2026-05-11 (rev150) — ingen autoplay. Ersatt 2026-10-28:
      "man klickar in för att spela" → autoplay ljudlöst (webbläsarna
      blockerar autoplay med ljud på mobil/Safari) via playVideo() i
      onReady, varje gång. Ljudet slås på i YouTubes kontroller.

      360°-pan: setupSphericalPan (Eric 2026-10-28) lägger ett draglager
      som styr setSphericalProperties; "360° on YouTube"-knappen finns kvar.
    */
    const autoplay = true;

    let playerId = early && early.id;
    if (!early) {
      const playerDiv = document.createElement('div');
      playerId = 'igelkott-yt-player-' + Math.random().toString(36).slice(2, 9);
      playerDiv.id = playerId;
      playerDiv.style.cssText = 'width:100%;height:100%;display:block;';
      target.appendChild(playerDiv);
    }
    target.dataset.videoHeroMounted = 'true';

    loadYouTubeIframeAPI()
      .then((YT) => {
        /*
          rev213 — spara player-instansen så trim-strippen (setupTrimStrip)
          kan läsa getCurrentTime/getDuration + seekTo. Ready-event
          dispatchas i onReady nedan när API:t faktiskt är anropbart.
        */
        heroPlayerInstance = createHeroPlayer(YT, early, playerId, {
          videoId: youtubeId,
          width: '100%',
          height: '100%',
          /*
            Eric 2026-05-11 (rev134) — Safari macOS-bug: YouTube-iframe
            paint:ar bara första frame medan timeline rör sig. Två kända
            orsaker:
              1. Safari "Prevent Cross-Site Tracking" blockerar YouTube-
                 cookies → embedded player can't auth → video-decode
                 startar inte (audio kan fungera).
              2. WebKit GPU compositing-stall — video-layer freezes
                 tills nästa compositing-pass (cursor-move, scroll, etc).
            youtube-nocookie.com är YouTube:s privacy-enhanced embed-host
            som undviker cross-site-tracking-cookies, kringgår orsak 1.
            (Compositing-stall hanteras separat i onReady nedan.)
          */
          host: 'https://www.youtube-nocookie.com',
          playerVars: {
            rel: 0,
            playsinline: 1,
            controls: 1,
            // Egen helskärmsknapp, se addFullscreenButton.
            fs: 0,
            iv_load_policy: 3,
            enablejsapi: 1,
            // autoplay-param av: uppspelningen startas ljudlöst i onReady (2026-10-28).
            autoplay: 0,
            mute: 0,
            // Eric 2026-05-02: tvinga UI-språk att matcha sajten istället för
            // YT:s auto-detect (som plockar Sweden-IP → svenska "Kvalitet"
            // etc, inkonsistent med engelsk storefront-chrome).
            hl: (window.igelkott && window.igelkott.shop && window.igelkott.shop.locale) || 'en',
            origin: window.location.origin,
          },
          events: {
            onReady: (event) => {
              /*
                Eric 2026-05-11 (rev131) — Emma-rapport: konsol-fel
                "Permission policy 'Fullscreen' check failed for document
                with origin 'https://www.youtube.com'". YouTube IFrame
                Player API skapar iframen själv och sätter inte alltid
                `fullscreen` i `allow`-attributet. Modern Permission
                Policy kräver det explicit. Vi patchar iframen i onReady
                (då den är fully laddad) med full media-permission-lista
                som matchar setupVideo:824. Detta dödar även "Unable to
                post message"-warning eftersom YT widget-api:n inte
                längre kollar för fullscreen-capability ett-frame-för-
                sent.
              */
              try {
                const iframe = typeof event.target.getIframe === 'function'
                  ? event.target.getIframe() : null;
                if (iframe) {
                  const existing = (iframe.getAttribute('allow') || '')
                    .split(';').map((s) => s.trim()).filter(Boolean);
                  const needed = [
                    'accelerometer', 'clipboard-write',
                    'encrypted-media', 'fullscreen', 'gyroscope',
                    'picture-in-picture', 'web-share',
                  ];
                  const merged = Array.from(new Set([...existing, ...needed]));
                  iframe.setAttribute('allow', merged.join('; '));
                  if (!iframe.hasAttribute('allowfullscreen')) {
                    iframe.setAttribute('allowfullscreen', '');
                  }
                  /*
                    Eric 2026-05-11 (rev149) — Safari ITP CORS-blocking:
                    youtubei/v1/log_event + googlevideo.com/videoplayback
                    failas i Safari pga cross-site-tracking-protection.
                    credentialless-attribut (Safari 17+, Chrome 110+) gör
                    iframe stateless (no cookies/storage) → ITP slappnar
                    av eftersom det inte är tracking-context längre.
                  */
                  if (!iframe.hasAttribute('credentialless')) {
                    iframe.setAttribute('credentialless', '');
                  }

                  /*
                    Eric 2026-05-11 (rev134) — Safari macOS: video-layer
                    freezes på första frame tills compositing-pass triggas.
                    Workaround: toggle:a transform/translateZ för att
                    tvinga GPU re-allokering av video-layern. Mac Safari
                    har ingen reliable userAgent-detection som inte även
                    matchar Chrome — vi använder vendor-pattern istället.
                    Trigger:as bara på AppleWebKit utan Chrome/Chromium
                    (= real Safari, inte Edge/Chrome/CiC).
                  */
                  const ua = navigator.userAgent;
                  const isSafari = /AppleWebKit/.test(ua) && !/Chrome|Chromium|Edg\//.test(ua);
                  if (isSafari) {
                    const origTransform = iframe.style.transform;
                    iframe.style.transform = 'translateZ(0)';
                    iframe.style.willChange = 'transform';
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        iframe.style.transform = origTransform || '';
                        iframe.style.willChange = '';
                      });
                    });
                    log.debug('product.safari_compositing_kick', {
                      handle: root.dataset.handle,
                    });
                  }
                }
              } catch (err) {
                log.debug('product.yt_iframe_attr_patch_failed', { error: err.message });
              }

              /*
                Eric 2026-05-02: bias YT playback-quality mot 4K. Default
                ABR väljer ofta 360-720p även om source har 4K. Vi har
                4K-uploads i ekosystemet (kollat via gear → Quality) så
                vi vill se max. setPlaybackQualityRange är fortfarande
                respekterad (setPlaybackQuality är deprecated). Värdet
                är en suggested-cap; YT väljer ändå lägre vid CPU/network-
                begränsning.
              */
              try {
                if (typeof event.target.setPlaybackQualityRange === 'function') {
                  event.target.setPlaybackQualityRange('hd2160');
                }
                if (typeof event.target.setPlaybackQuality === 'function') {
                  event.target.setPlaybackQuality('hd2160');
                }
              } catch (err) {
                log.debug('product.yt_quality_set_failed', { error: err.message });
              }
              log.info('product.video_hero_mounted', {
                handle: root.dataset.handle,
                youtube_id: youtubeId,
                autoplay: !!autoplay,
              });
              /*
                rev213 — signalera player-ready till trim-strippen.
                event.target är den anropbara player-instansen (samma
                objekt som heroPlayerInstance men garanterat initierad).
              */
              if (FLAT_YOUTUBE_UA) {
                mountStillFallback(root, target, event.target, youtubeId);
                return;
              }
              setupSphericalPan(target, event.target);
              if (autoplay) {
                try {
                  event.target.mute();
                  event.target.playVideo();
                } catch (err) {
                  log.debug('product.autoplay_failed', { error: err.message });
                }
              }
              document.dispatchEvent(new CustomEvent('igelkott-product-player-ready', {
                detail: { player: event.target },
              }));
            },
            onError: (event) => {
              log.warn('product.player_error', {
                handle: root.dataset.handle,
                code: event.data,
              });
            },
          },
        });
      })
      .catch((err) => {
        log.error('product.yt_api_load_failed', {
          handle: root.dataset.handle,
          error: err.message,
        });
      });

    /*
      "360° on YouTube"-knappen borttagen (Eric 2026-10-28, "ta bort
      youtubeknappen"): 360-pan och gyro fungerar nu i spelaren på sidan
      (setupSphericalPan). buildYouTubeToggle finns kvar oanropad om den
      ska tillbaka.
    */

    return true;
  }

  /**
   * Komplettera spec-tabellen med rader parsed från body_html om
   * metafields inte täcker dem. Vi prefer:ar metafields (de är
   * strukturerade) men body_html är fallback för plates som inte har
   * metafields populated än.
   */
  function fillSpecsFromBody(root, parsedSpecs) {
    if (!parsedSpecs.length) return;
    const list = root.querySelector('.igelkott-product__specs-list');
    if (!list) return;

    // Befintliga labels från metafield-rendering — case-insensitive set
    const existing = new Set(
      Array.from(list.querySelectorAll('dt')).map((dt) =>
        (dt.textContent || '').trim().toLowerCase()
      )
    );

    let added = 0;
    parsedSpecs.forEach(({ label, value }) => {
      const key = label.toLowerCase();
      if (existing.has(key)) return; // metafield-versionen vinner
      const wrap = document.createElement('div');
      wrap.className = 'igelkott-product__spec';
      const dt = document.createElement('dt');
      dt.textContent = label;
      const dd = document.createElement('dd');
      dd.textContent = value;
      wrap.appendChild(dt);
      wrap.appendChild(dd);
      list.appendChild(wrap);
      added++;
    });

    if (added > 0) {
      log.info('product.specs_from_body', {
        handle: root.dataset.handle,
        added_count: added,
      });
    }
  }

  function init() {
    const root = document.querySelector('[data-igelkott-product]');
    if (!root) return;

    // Parse body_html FÖRE allt annat så vi kan styra hero
    const parsed = parseBodyHtml(root);
    const { specs, intro } = parsed;
    // Sektionen skriver inte längre ut Publisher-iframen i body-källan;
    // id:t kommer då i data-body-youtube-id (samma iframe, samma id).
    const youtubeId = parsed.youtubeId || root.dataset.bodyYoutubeId || null;
    renderIntro(root, intro);
    renderSeriesStrip(root);

    // Hero-prioritering (samma på mobil + desktop):
    //   1. YouTube-video om youtube_id finns → 16:9 iframe
    //   2. Annars: PSV med equirectangular-strippen som fallback
    // Tidigare hade vi separata flöden per viewport (mobil = YouTube,
    // desktop = PSV) men Eric vill ha YouTube överallt nu eftersom
    // det är produktens primära demo. PSV är bara fallback om
    // Publisher inte har stamplat youtube_id.
    const videoMounted = setupVideoHero(root, youtubeId);
    if (!videoMounted) {
      mountViewer(root);
    } else {
      // rev213 — trim-strip bara i video-hero-läget (YT IFrame API krävs
      // för tid/seek). PSV-fallback-plates saknar timeline att trimma.
      setupTrimStrip(root);
    }

    // Override metafield-youtube_id med body_html-extracted om det
    // saknades — så Watch-Video-button funkar även utan metafield.
    if (youtubeId && !root.dataset.youtubeId) {
      root.dataset.youtubeId = youtubeId;
    }

    setupVariants(root);
    setupActions(root);
    setupProductShare(root);
    setupFreeDemoButton(root);
    setupVideo(root);
    fillSpecsFromBody(root, specs);
    trackView(root.dataset.handle);

    log.info('product.initialized', {
      handle: root.dataset.handle,
      hero: videoMounted ? 'youtube' : 'psv',
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

